/**
 * ServiceDesignerGraph — visual graph editor for one service.
 *
 * - Loads the real graph from /api/service-designer/services/<id>/graph
 * - Search box adds EXISTING CIs (from /api/ci/search) — no duplicates
 * - Click two nodes to connect them with a typed relation
 *   (/api/service-designer/services/<id>/connect)
 * - Expand/contract nested services with a depth control
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  CheckIcon,
  ArrowPathIcon,
  ArrowLeftOnRectangleIcon,
  TrashIcon,
  XMarkIcon,
  Squares2X2Icon,
  DocumentTextIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ServiceDesignerModelApplyModal from './ServiceDesignerModelApplyModal';
import ServiceConfigView from './ServiceConfigView';
import './ServiceDesigner.css';
import ContactsPanel from './ContactsPanel';

const NODE_COLORS = {
  service: '#7c3aed',
  rack_element: '#2563eb',
  virt_vm: '#0d9488',
  printer: '#b45309',
  wifi_controller: '#c2410c',
  wifi_access_point: '#c2410c',
  firewall: '#dc2626',
  router: '#7c3aed',
  workstation: '#4b5563',
  device: '#6b7280',
  storage_disk: '#0891b2',
  isp_circuit: '#65a30d',
  ipam_ip: '#475569',
  ipam_subnet: '#0ea5e9',
  logical_component: '#ec4899',
};

const CI_TYPE_LABELS = {
  rack_element: 'Servidor',
  server: 'Servidor',
  vm: 'VM',
  virt_vm: 'VM',
  printer: 'Impresora',
  wifi_controller: 'Controladora WiFi',
  wifi_access_point: 'AP WiFi',
  firewall: 'Firewall',
  router: 'Router',
  ups: 'SAI / UPS',
  workstation: 'Puesto',
  device: 'Dispositivo',
  storage_disk: 'Disco',
  isp_circuit: 'Circuito ISP',
  service: 'Servicio',
  ipam_ip: 'IP',
  ipam_subnet: 'Subred IPAM',
  virt_hypervisor: 'Hypervisor',
  virt_cluster: 'Clúster virtual',
  virt_datastore: 'Datastore',
  storage_lun: 'LUN',
  server_volume: 'Volumen',
  wifi_ssid: 'SSID WiFi',
  service_farm: 'Granja de servidores',
  rack: 'Rack',
  database_instance: 'Base de datos',
  instance_database: 'Base de datos alojada',
  logical_component: 'Grupo',
};

// Tipos de CI que el catálogo de empresa permite listar/filtrar. Incluye todos
// los que el diseñador puede resolver como miembro (servidores, VMs,
// hipervisores, clústeres, LUN, racks, granjas…).
const SEARCHABLE_TYPES = [
  'rack_element', 'virt_vm', 'virt_hypervisor', 'virt_cluster', 'virt_datastore',
  'printer', 'wifi_controller', 'wifi_access_point', 'wifi_ssid', 'firewall', 'router',
  'ups', 'workstation', 'storage_disk', 'storage_lun', 'server_volume', 'isp_circuit',
  'service', 'service_farm', 'rack', 'ipam_subnet', 'ipam_ip', 'database_instance',
];

// Alias antiguos que quedaron guardados en memberships/relaciones.
const CI_TYPE_ALIASES = {
  vm: 'virt_vm', server: 'rack_element', wlc: 'wifi_controller',
  ap: 'wifi_access_point', lun: 'storage_lun',
};
const nodeIdsForCi = (ciType, ciId) => {
  const ids = [`${ciType}:${ciId}`];
  const alias = CI_TYPE_ALIASES[ciType];
  if (alias) ids.push(`${alias}:${ciId}`);
  Object.entries(CI_TYPE_ALIASES).forEach(([short, real]) => {
    if (real === ciType) ids.push(`${short}:${ciId}`);
  });
  return ids;
};

// Iconos disponibles para los grupos lógicos (los asistentes ya ponen el suyo).
const GROUP_ICONS = ['🗂️', '🛡️', '📶', '🔐', '🌐', '💾', '🖥️', '📡', '🏢', '⚙️'];

// Metadatos por tipo REAL del rack (element_type) / CI: color, icono y etiqueta.
// tier = fila del layout por capas (0 arriba … 4 abajo) para leer de servicio
// a alimentación física de un vistazo.
const KIND_META = {
  server: { color: '#2563eb', icon: '🖥️', label: 'Servidor', tier: 3 },
  pdu: { color: '#ea580c', icon: '⚡', label: 'PDU (alimentación)', tier: 4 },
  switch: { color: '#0e7490', icon: '🔀', label: 'Switch', tier: 3 },
  ont: { color: '#0284c7', icon: '🛰️', label: 'ONT (fibra)', tier: 4 },
  cpe: { color: '#0369a1', icon: '📡', label: 'CPE / router del operador', tier: 4 },
  router: { color: '#7c3aed', icon: '🌐', label: 'Router', tier: 3 },
  storage: { color: '#65a30d', icon: '💽', label: 'Storage', tier: 3 },
  panel: { color: '#6b7280', icon: '🧩', label: 'Patch panel', tier: 3 },
  tray: { color: '#6b7280', icon: '🗄️', label: 'Bandeja', tier: 3 },
  rack_element: { color: '#2563eb', icon: '🖥️', label: 'Servidor', tier: 3 },
  virt_vm: { color: '#0d9488', icon: '🧊', label: 'Máquina virtual', tier: 2 },
  vm: { color: '#0d9488', icon: '🧊', label: 'Máquina virtual', tier: 2 },
  service: { color: '#7c3aed', icon: '🧩', label: 'Servicio', tier: 1 },
  printer: { color: '#b45309', icon: '🖨️', label: 'Impresora', tier: 2 },
  wifi_controller: { color: '#c2410c', icon: '📶', label: 'Controladora WiFi', tier: 2 },
  wifi_access_point: { color: '#c2410c', icon: '📡', label: 'Punto de acceso WiFi', tier: 3 },
  firewall: { color: '#dc2626', icon: '🛡️', label: 'Firewall', tier: 3 },
  ups: { color: '#f59e0b', icon: '🔋', label: 'SAI / UPS', tier: 4 },
  workstation: { color: '#4b5563', icon: '💻', label: 'Puesto', tier: 2 },
  device: { color: '#6b7280', icon: '📦', label: 'Dispositivo', tier: 2 },
  storage_disk: { color: '#0891b2', icon: '💽', label: 'Disco', tier: 3 },
  storage_lun: { color: '#65a30d', icon: '💾', label: 'LUN de cabina', tier: 3 },
  server_volume: { color: '#0d9488', icon: '🗂️', label: 'Volumen del equipo', tier: 2 },
  isp_circuit: { color: '#65a30d', icon: '🔗', label: 'Circuito ISP', tier: 3 },
  ipam_ip: { color: '#475569', icon: '🔢', label: 'Dirección IP', tier: 2 },
  ipam_subnet: { color: '#0ea5e9', icon: '🗺️', label: 'Subred IPAM', tier: 3 },
  virt_hypervisor: { color: '#7c3aed', icon: '⚙️', label: 'Hypervisor', tier: 2 },
  hypervisor: { color: '#7c3aed', icon: '⚙️', label: 'Hypervisor', tier: 2 },
  virt_cluster: { color: '#7c3aed', icon: '🧱', label: 'Clúster virtual', tier: 2 },
  virt_datastore: { color: '#65a30d', icon: '🗄️', label: 'Datastore', tier: 3 },
  wifi_ssid: { color: '#c2410c', icon: '📶', label: 'SSID WiFi', tier: 2 },
  rack: { color: '#0e7490', icon: '🗃️', label: 'Rack', tier: 4 },
  database_instance: { color: '#0891b2', icon: '🗄️', label: 'Instancia de BD', tier: 2 },
  instance_database: { color: '#0e7490', icon: '🗃️', label: 'Base de datos', tier: 3 },
  logical_component: { color: '#ec4899', icon: '🗂️', label: 'Grupo lógico', tier: 0 },
  // Nodos derivados de la topología AD de un servicio (no son CIs: el motor los
  // genera desde Service.config_json.ad_topology, pestaña "Topología AD").
  ad_domain: { color: '#7c3aed', icon: '🏰', label: 'Dominio AD', tier: 1 },
  ad_site: { color: '#0d9488', icon: '🏢', label: 'Sede AD', tier: 2 },
  ad_ou: { color: '#b45309', icon: '📁', label: 'Unidad organizativa', tier: 3 },
  ad_gpo: { color: '#6366f1', icon: '📜', label: 'GPO', tier: 4 },
  // Alojamiento de una máquina DELEGADA: el hipervisor/servidor/rack son del
  // proveedor, así que el motor corta la cadena y deja este nodo genérico.
  external_infra: { color: '#64748b', icon: '🏢', label: 'Infraestructura externa', tier: 3 },
};
// Nodos que son de tipo `service` pero con un tipo de servicio propio: el motor
// envía `service_type` en el nodo para poder pintarlos con su icono (p. ej. el
// RADIUS es un servicio AAA, no un "Servicio" genérico).
const SERVICE_TYPE_META = {
  radius: { icon: '🔐', label: 'RADIUS / AAA' },
  active_directory: { icon: '🏰', label: 'Active Directory' },
  backup_software: { icon: '🛡️', label: 'Software de backup' },
  virtualization: { icon: '⚙️', label: 'Virtualización' },
};
// Los nombres de tarjeta tienen un ancho fijo (unos 104 px). En vez de cortar
// siempre con «…», se reduce la letra en los nombres medianos; los muy largos
// conservan el tooltip con el nombre completo.
function fitCardFontSize(text, base = 13, comfortable = 14, min = 9.5) {
  const len = Array.from(String(text || '')).length;
  if (len <= comfortable) return base;
  return Math.max(min, Math.round((base * comfortable / len) * 10) / 10);
}

// Hit-test de grupos en coordenadas de PANTALLA, tanto para arrastrar nodos
// como para soltar un CI desde el catálogo de la derecha dentro de un grupo.
function groupIdAtPoint(clientX, clientY) {
  let gid = null;
  document.querySelectorAll('.sd-group-box[data-gid], .sd-node-group[data-gid]').forEach((el) => {
    const r = el.getBoundingClientRect();
    if (clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom) {
      gid = el.getAttribute('data-gid');
    }
  });
  return gid;
}

function metaFor(n) {
  const st = SERVICE_TYPE_META[(n && n.service_type) || ''] || null;
  // El nodo raíz de un grafo de SERVICIO se pinta como servicio; el de un grafo
  // centrado en un elemento (is_focus) conserva su tipo real (VM, cabina…).
  if (n.is_root && !n.is_focus) {
    return { ...(KIND_META.service), tier: 0, icon: (st && st.icon) || '🧩', label: (st && st.label) || 'Servicio' };
  }
  const base = KIND_META[n.kind] || KIND_META[n.ci_type] || {
    color: NODE_COLORS[n.ci_type] || '#64748b',
    icon: '📦',
    label: CI_TYPE_LABELS[n.ci_type] || n.ci_type || 'CI',
    tier: 2,
  };
  // Las tarjetas-resumen (políticas, grupos con icono propio) mandan su icono.
  const withIcon = n.icon ? { ...base, icon: n.icon } : base;
  return st ? { ...withIcon, icon: st.icon, label: st.label } : withIcon;
}

// Ayuda breve para los tipos de relación por defecto (spec §2B).
const RELATION_HELP = {
  depends_on: 'A necesita a B para funcionar. Ej.: la app depende de la base de datos.',
  uses: 'A emplea/consume B (dependencia ligera). Ej.: la app usa el servicio de ficheros.',
  contains: 'A incluye a B como parte suya (1:N). Ej.: la granja contiene los servidores RDS.',
  runs_on: 'A se ejecuta sobre B. Ej.: la VM se ejecuta en el servidor físico.',
  protected_by: 'A está resguardada/expuesta tras B. Ej.: el servicio está protegido por la VPN.',
  authenticated_by: 'A está autenticado por B (B es su servidor de autenticación). Ej.: el RADIUS está autenticado por Active Directory.',
  authenticates_on: 'A se valida en B. Ej.: el AP se autentica en el servidor RADIUS.',
  has_backup: 'A tiene sus copias gestionadas por B.',
  has_policy: 'El servicio tiene este trabajo/política de backup.',
  applies_to: 'La política o el grupo se aplica a esos equipos.',
  communicates_with: 'A y B intercambian tráfico (simétrica, sin jerarquía).',
  provides: 'A ofrece capacidad/servicio a B. Ej.: la VPN proporciona acceso al servicio.',
  member_of: 'A pertenece a B (agrupación/pertenencia).',
  uses_lun: 'El volumen del equipo se sirve de ese LUN de la cabina.',
  exported_by: 'Ese LUN lo publica (exporta) esa cabina.',
  uses_storage: 'El equipo usa almacenamiento de esa cabina (cadena derivada de sus volúmenes).',
};

// Categorías visuales de relación: color + etiqueta legible + estilo de línea.
const EDGE_CATEGORIES = {
  dependency: { color: '#3b82f6', label: 'Dependencia', dashed: false },
  hosting: { color: '#14b8a6', label: 'Ejecución / hosting', dashed: false },
  power: { color: '#f97316', label: 'Alimentación', dashed: false },
  replica: { color: '#a855f7', label: 'Replicación', dashed: true },
  security: { color: '#ef4444', label: 'Seguridad / acceso', dashed: false },
  comms: { color: '#06b6d4', label: 'Comunicación', dashed: true },
  provides: { color: '#22c55e', label: 'Provee', dashed: false },
  backup: { color: '#64748b', label: 'Copia de seguridad', dashed: false },
  storage: { color: '#65a30d', label: 'Almacenamiento', dashed: false },
  structural: { color: '#94a3b8', label: 'Pertenencia / agrupación', dashed: true },
};
const REL_TO_CATEGORY = {
  depends_on: 'dependency', required_by: 'dependency', uses: 'dependency', used_by: 'dependency',
  runs_on: 'hosting', runs: 'hosting',
  powered_by: 'power',
  replica_to: 'replica',
  protected_by: 'security', protects: 'security', authenticated_by: 'security', authenticates: 'security',
  authenticates_on: 'security', authenticated_from: 'security',
  communicates_with: 'comms',
  provides: 'provides', provided_by: 'provides',
  has_backup: 'backup', backs_up: 'backup',
  has_policy: 'backup', policy_of: 'backup',
  applies_to: 'backup', applied_by: 'backup',
  stores_on: 'backup',
  uses_lun: 'storage', used_by_volume: 'storage',
  exported_by: 'storage', presents: 'storage',
  uses_storage: 'storage', used_by_host: 'storage', mounts: 'storage',
  contains: 'structural', part_of: 'structural', member_of: 'structural', has_member: 'structural',
};
function edgeCategory(e) {
  if (e.kind === 'membership') return 'structural';
  return REL_TO_CATEGORY[e.relation_type] || 'dependency';
}

// Genera un redactado narrativo del montaje del servicio a partir del grafo.
function buildNarrative(nodes, edges, service, focusNode) {
  if (!nodes || !nodes.length) return [];
  const byId = {};
  nodes.forEach((n) => { byId[n.id] = n; });
  const nm = (id) => {
    const n = byId[id];
    if (!n) return id;
    return n.name || n.logical_label || `${(KIND_META[n.kind] || KIND_META[n.ci_type] || {}).label || 'CI'} ${n.ci_id}`;
  };
  const typeLabel = (n) => (n.is_root ? 'servicio' : metaFor(n).label || n.ci_type);
  const rels = edges.filter((e) => e.kind !== 'membership');
  const byRel = (name) => rels.filter((e) => e.relation_type === name);

  const paras = [];

  // Intro: composición por tipo
  const PLURALS = {
    'Servicio': 'servicios', 'Grupo lógico': 'grupos lógicos', 'Máquina virtual': 'máquinas virtuales',
    'Servidor': 'servidores', 'PDU (alimentación)': 'PDUs', 'Switch': 'switches', 'Router': 'routers',
    'Storage': 'cabinas de almacenamiento', 'Controladora WiFi': 'controladoras WiFi',
    'Punto de acceso WiFi': 'puntos de acceso WiFi', 'Firewall': 'firewalls', 'Puesto': 'puestos',
    'Impresora': 'impresoras', 'Circuito ISP': 'circuitos ISP', 'Disco': 'discos', 'Dispositivo': 'dispositivos',
    'LUN de cabina': 'LUNs', 'Volumen del equipo': 'volúmenes',
    'RADIUS / AAA': 'servidores RADIUS', 'Active Directory': 'controladores de dominio',
    'Instancia de BD': 'instancias de BD', 'Base de datos': 'bases de datos',
    'Infraestructura externa': 'infraestructuras externas',
  };
  const plural = (label, n) => (n === 1 ? label.toLowerCase() : (PLURALS[label] || label.toLowerCase() + 's'));
  const counts = {};
  nodes.forEach((n) => {
    if (n.is_root) return;
    const lbl = typeLabel(n);
    counts[lbl] = (counts[lbl] || 0) + 1;
  });
  const compo = Object.entries(counts).map(([k, v]) => `${v} ${plural(k, v)}`);
  const svcName = service?.display_name || service?.name || 'Servicio';
  const focusId = focusNode && focusNode.id;
  if (focusId) {
    // Grafo centrado en un elemento (spec §18): de qué depende / dónde impacta
    const n1 = nodes.length - 1;
    paras.push(
      `«${nm(focusId)}» se relaciona con ${n1} elemento${n1 === 1 ? '' : 's'}` +
      (compo.length ? `: ${compo.join(', ')}.` : '.')
    );
    const fmt = (e, id) => `«${nm(id)}» (${e.relation_display || e.relation_type})`;
    const outs = rels
      .filter((e) => e.source === focusId && Number(e.level) === 1)
      .map((e) => fmt(e, e.target));
    const ins = rels
      .filter((e) => e.target === focusId && Number(e.level) === 1)
      .map((e) => fmt(e, e.source));
    if (outs.length) paras.push(`De qué depende: ${outs.join(', ')}.`);
    if (ins.length) paras.push(`Dónde impacta: ${ins.join(', ')}.`);
  } else {
    paras.push(
      `El servicio «${svcName}» está formado por ${nodes.length - 1} elemento${nodes.length - 1 === 1 ? '' : 's'}` +
      (compo.length ? `: ${compo.join(', ')}.` : '.')
    );
  }

  // Grupos lógicos
  nodes.filter((n) => n.is_logical).forEach((g) => {
    const contained = rels
      .filter((e) => e.relation_type === 'contains' && e.source === g.id)
      .map((e) => nm(e.target));
    if (contained.length) {
      paras.push(`El grupo «${nm(g.id)}» reúne: ${contained.join(', ')}.`);
    }
  });

  // Bases de datos alojadas DENTRO de una instancia (segundo nivel)
  const instDb = rels.filter(
    (e) => e.relation_type === 'contains' && byId[e.source] && byId[e.source].ci_type === 'database_instance'
  );
  if (instDb.length) {
    const porInstancia = {};
    instDb.forEach((e) => { (porInstancia[e.source] = porInstancia[e.source] || []).push(nm(e.target)); });
    Object.entries(porInstancia).forEach(([src, dbs]) => {
      paras.push(`La instancia «${nm(src)}» contiene ${dbs.length} base${dbs.length === 1 ? '' : 's'} de datos: ${dbs.join(', ')}.`);
    });
  }

  // Cadenas extremo a extremo: A se ejecuta en SERVIDOR, que se alimenta de PDU
  const runsOn = byRel('runs_on');
  const poweredBy = byRel('powered_by');
  const powerOf = {};
  poweredBy.forEach((e) => { powerOf[e.source] = e.target; });
  const chains = runsOn.map((e) => {
    let s = `«${nm(e.source)}» se ejecuta en «${nm(e.target)}»`;
    if (powerOf[e.target]) s += `, que recibe alimentación de «${nm(powerOf[e.target])}»`;
    return s + '.';
  });
  if (chains.length) {
    paras.push('Despliegue físico (extremo a extremo): ' + chains.join(' '));
  } else if (poweredBy.length) {
    paras.push('Alimentación: ' + poweredBy.map((e) => `«${nm(e.source)}» se alimenta de «${nm(e.target)}».`).join(' '));
  }

  // Dependencias
  const deps = [...byRel('depends_on'), ...byRel('uses')];
  if (deps.length) {
    paras.push('Dependencias: ' + deps.map((e) => `«${nm(e.source)}» ${e.relation_display || 'depende de'} «${nm(e.target)}».`).join(' '));
  }
  // Replicación
  const reps = byRel('replica_to');
  if (reps.length) {
    paras.push('Replicación: ' + reps.map((e) => `«${nm(e.source)}» replica hacia «${nm(e.target)}».`).join(' '));
  }
  // Copias de seguridad
  const backups = byRel('has_backup');
  const stores = byRel('stores_on');
  if (backups.length || stores.length) {
    const parts = [];
    if (backups.length) {
      const byT = {};
      backups.forEach((e) => { (byT[e.target] = byT[e.target] || []).push(nm(e.source)); });
      Object.entries(byT).forEach(([tgt, srcs]) => {
        parts.push(`Se respalda${srcs.length > 1 ? 'n' : ''} ${srcs.map((s) => `«${s}»`).join(', ')} en «${nm(tgt)}».`);
      });
    }
    stores.forEach((e) => { parts.push(`«${nm(e.source)}» almacena las copias en «${nm(e.target)}».`); });
    paras.push('Copias de seguridad: ' + parts.join(' '));
  }
  // Almacenamiento: cadena equipo → volumen → LUN → cabina (derivada de datos)
  const storageEdges = [...byRel('uses_storage'), ...byRel('mounts'), ...byRel('uses_lun')];
  if (storageEdges.length) {
    const parts = [];
    byRel('uses_storage').forEach((e) => {
      const detail = Array.isArray(e.detail) ? e.detail : [];
      const unidades = detail
        .map((d) => `${d.mount_point || d.label || 'volumen'}${d.size_gb ? ` (${d.size_gb} GB${d.multipath ? ', multipath' : ''})` : ''}${d.lun_name ? ` ← ${d.lun_name}` : ''}`)
        .join('; ');
      parts.push(
        `«${nm(e.source)}» usa almacenamiento de «${nm(e.target)}»` +
        (unidades ? `: ${unidades}.` : '.')
      );
    });
    byRel('mounts').forEach((e) => parts.push(`«${nm(e.source)}» tiene la unidad «${nm(e.target)}».`));
    byRel('uses_lun').forEach((e) => parts.push(`«${nm(e.source)}» se sirve del LUN «${nm(e.target)}».`));
    paras.push('Almacenamiento: ' + parts.join(' '));
  }

  // Direccionamiento: quién sirve el DHCP de cada subred y sus opciones. Las
  // aristas las deriva el backend del propio scope (rangos dhcp con su
  // elemento servidor y sus DNS/NTP enlazados).
  const dhcpEdges = rels.filter((e) => e.relation_display === 'proporciona DHCP');
  const dnsEdges = rels.filter((e) => String(e.relation_display || '').startsWith('usa DNS'));
  const ntpEdges = rels.filter((e) => String(e.relation_display || '').startsWith('usa NTP'));
  if (dhcpEdges.length || dnsEdges.length || ntpEdges.length) {
    const parts = [];
    dhcpEdges.forEach((e) => {
      const d = Array.isArray(e.detail) ? e.detail[0] : null;
      const opciones = [];
      if (d?.pool) opciones.push(`pool ${d.pool}`);
      if (d?.gateway) opciones.push(`gateway ${d.gateway}`);
      if (d?.domain_name) opciones.push(`dominio ${d.domain_name}`);
      if (d?.dns?.length) opciones.push(`DNS ${d.dns.join(', ')}`);
      if (d?.ntp?.length) opciones.push(`NTP ${d.ntp.join(', ')}`);
      parts.push(
        `«${nm(e.source)}» sirve el DHCP de «${nm(e.target)}»` +
        (opciones.length ? `: ${opciones.join(' · ')}.` : '.')
      );
    });
    dnsEdges.forEach((e) => parts.push(`«${nm(e.source)}» resuelve nombres con «${nm(e.target)}».`));
    ntpEdges.forEach((e) => parts.push(`«${nm(e.source)}» toma la hora de «${nm(e.target)}».`));
    paras.push('Direccionamiento y DHCP: ' + parts.join(' '));
  }

  // Seguridad / acceso
  const sec = [...byRel('protected_by'), ...byRel('authenticated_by')];
  if (sec.length) {
    paras.push('Seguridad y acceso: ' + sec.map((e) => `«${nm(e.source)}» ${e.relation_display || 'protegido por'} «${nm(e.target)}».`).join(' '));
  }
  // Comunicación
  const comm = byRel('communicates_with');
  if (comm.length) {
    paras.push('Comunicaciones: ' + comm.map((e) => `«${nm(e.source)}» se comunica con «${nm(e.target)}».`).join(' '));
  }
  // Contains a nivel de infra (controladora → APs)
  const contNonLogical = rels.filter((e) => e.relation_type === 'contains' && byId[e.source] && !byId[e.source].is_logical);
  if (contNonLogical.length) {
    const bySrc = {};
    contNonLogical.forEach((e) => { (bySrc[e.source] = bySrc[e.source] || []).push(nm(e.target)); });
    Object.entries(bySrc).forEach(([src, tgts]) => {
      paras.push(`«${nm(src)}» integra: ${tgts.join(', ')}.`);
    });
  }
  return paras;
}

function cyLabel(name, max = 26) {
  const s = String(name || '');
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

// Recorta una arista para que sus extremos queden en el borde del nodo
// (radio del círculo ≈26px) y la flecha no quede tapada por el propio nodo.

// Path con tangentes VERTICALES: las líneas salen por abajo del nodo origen y
// entran por arriba del destino, fluyendo de arriba abajo como un diagrama de
// árbol limpio. `curve` desplaza los puntos de control en horizontal para
// separar aristas paralelas entre el mismo par de nodos.
function edgePath(x1, y1, x2, y2, curve) {
  const dy = y2 - y1;
  const k = Math.max(26, Math.abs(dy) * 0.5);
  const dir = dy >= 0 ? 1 : -1;
  const c1x = x1 + curve, c1y = y1 + dir * k;
  const c2x = x2 + curve, c2y = y2 - dir * k;
  return `M ${x1} ${y1} C ${c1x} ${c1y} ${c2x} ${c2y} ${x2} ${y2}`;
}

// Punto medio (t=0.5) de la cúbica anterior, para colocar la etiqueta sobre
// la línea sin que se despegue de ella.
function curveMid(x1, y1, x2, y2, curve) {
  const dy = y2 - y1;
  const k = Math.max(26, Math.abs(dy) * 0.5);
  const dir = dy >= 0 ? 1 : -1;
  const c1x = x1 + curve, c1y = y1 + dir * k;
  const c2x = x2 + curve, c2y = y2 - dir * k;
  return {
    x: 0.125 * x1 + 0.375 * c1x + 0.375 * c2x + 0.125 * x2,
    y: 0.125 * y1 + 0.375 * c1y + 0.375 * c2y + 0.125 * y2,
  };
}

// Punto en el borde de una tarjeta (rectángulo hw×hh) en dirección a (tx,ty).
function rectBorderPoint(cx, cy, tx, ty, hw, hh) {
  const dx = tx - cx, dy = ty - cy;
  if (dx === 0 && dy === 0) return { x: cx, y: cy };
  const scale = 1 / Math.max(Math.abs(dx) / hw, Math.abs(dy) / hh);
  return { x: cx + dx * scale, y: cy + dy * scale };
}

export default function ServiceDesignerGraph({
  serviceId, companyId, onBack, onOpenService,
  navStack, onNavigateToIndex, onGoToList,
  focusCi, onFocusCi, onExitFocus, onExitFocusAll, onFocusToIndex, focusTrail,
}) {
  const [service, setService] = useState(null);
  const [graph, setGraph] = useState(null);
  const [relationTypes, setRelationTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [depth, setDepth] = useState('0');

  // ── Modo "diagrama de un elemento" (spec §18): grafo centrado en un CI, con
  // de qué depende y dónde impacta. Es de SÓLO LECTURA: las relaciones y la
  // pertenencia se editan en la vista del servicio.
  const isFocus = Boolean(focusCi && focusCi.ciId != null);
  const readOnly = isFocus;
  const focusKey = isFocus ? `${focusCi.ciType}:${focusCi.ciId}` : null;
  const [focusDepth, setFocusDepth] = useState('1');
  const [focusDirection, setFocusDirection] = useState('both');

  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  // Tipos activos del catálogo (checkboxes). Vacío = todos visibles.
  const [hiddenTypes, setHiddenTypes] = useState({});

  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [connectTarget, setConnectTarget] = useState(null);
  const [relationTypeId, setRelationTypeId] = useState('');
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);
  const [connectDirection, setConnectDirection] = useState('a_to_b'); // 'a_to_b' | 'b_to_a'
  const [connectBidirectional, setConnectBidirectional] = useState(false);
  const [connNodeId, setConnNodeId] = useState(null); // nodo cuyo detalle de conexiones se muestra
  const [showModelApply, setShowModelApply] = useState(false);
  const [showLogicalInput, setShowLogicalInput] = useState(false); // añadir componente lógico
  const [logicalLabel, setLogicalLabel] = useState('');
  const [logicalIcon, setLogicalIcon] = useState(GROUP_ICONS[0]);
  const [logicalDetail, setLogicalDetail] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showNarrative, setShowNarrative] = useState(false);
  const [showLegend, setShowLegend] = useState(true);
  const [showAllMembership, setShowAllMembership] = useState(false);
  // Nodos ocultos manualmente (👁): el grafo solo pinta los visibles. Se
  // persisten en el backend junto con el layout (Service.config_json.
  // service_designer.hidden) para que sobrevivan a una recarga.
  const [hiddenIds, setHiddenIds] = useState([]);
  // Grupos lógicos plegados: se pinta la caja como resumen y sus miembros no
  // ocupan espacio. Se persisten junto al layout, como `hidden`.
  const [collapsedIds, setCollapsedIds] = useState([]);
  // Flechas que genera el diseñador (hosting, almacenamiento, AD…): se pueden
  // ocultar una a una y la elección se guarda con el servicio.
  const [hiddenDerived, setHiddenDerived] = useState([]);
  const [showDerivedPanel, setShowDerivedPanel] = useState(false);
  // Relación tipada que se está editando (tipo/sentido/flecha doble).
  const [editRel, setEditRel] = useState(null);
  const [editRelSaving, setEditRelSaving] = useState(false);
  const [showHiddenPanel, setShowHiddenPanel] = useState(false);
  // Aviso al desplegar un grupo grande: se puede elegir qué miembros mostrar.
  const [expandPrompt, setExpandPrompt] = useState(null);
  // Modal de tipos del catálogo (sustituye la fila de chips por un botón).
  const [showTypeModal, setShowTypeModal] = useState(false);
  // Detalle del elemento (clic en el nombre de una tarjeta)
  const navigate = useNavigate();
  const [detailNode, setDetailNode] = useState(null);
  const [detailInfo, setDetailInfo] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailNote, setDetailNote] = useState(null);
  const [detailIps, setDetailIps] = useState(null);   // IPs (IPAM) del elemento
  const pageRef = useRef(null);

  const svgRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);

  // ── Drag & drop: CI de la búsqueda al canvas + recolocación de nodos ─────
  const [draggingCi, setDraggingCi] = useState(null);   // CI arrastrado desde la búsqueda
  const [canDropCi, setCanDropCi] = useState(false);    // resaltar el canvas como zona de drop
  const dragEnterDepth = useRef(0);
  const [userPos, setUserPos] = useState({});           // posiciones recolocadas (persistidas)
  const userPosRef = useRef({});                        // espejo sincrono de userPos
  const moveDragRef = useRef(null);                     // estado del arrastre de un nodo
  const [moveDrag, setMoveDrag] = useState(null);       // feedback visual {nodeId, x, y}
  const [dropGid, setDropGid] = useState(null);         // caja de grupo resaltada al arrastrar encima
  const [escapeHint, setEscapeHint] = useState(false);  // “suelta para sacar” al arrastrar fuera
  const zoomRef = useRef(1);
  zoomRef.current = zoom;
  const suppressClickRef = useRef(false);

  // ── Detalle de un elemento (panel lateral) ──────────────────────────────
  // Ruta del módulo CMDB donde se gestiona cada tipo de CI.
  const ciRoute = (n) => {
    if (!n || n.is_logical) return null;
    if (n.ci_type === 'rack_element') {
      const k = n.kind || 'server';
      switch (k) {
        case 'storage': return '/infrastructure/storage-arrays';
        case 'switch': return '/infrastructure/switches';
        case 'pdu': return '/infrastructure/pdus';
        case 'ups': return '/infrastructure/ups';
        case 'panel': return '/infrastructure/patch-panels';
        case 'router': return '/infrastructure/routers';
        default: return '/infrastructure/servers';
      }
    }
    const routes = {
      vm: '/virt/vms', server: '/infrastructure/servers',
      virt_vm: '/virt/vms', firewall: '/infrastructure/firewalls', service: '/services',
      printer: '/infrastructure/printers', workstation: '/infrastructure/workstations/list',
      wifi_controller: '/wifi/controllers', wifi_access_point: '/wifi/aps',
      storage_disk: '/infrastructure/storage-arrays', isp_circuit: '/wan/circuits',
      router: '/wan/routers', ups: '/infrastructure/ups', ipam_ip: '/networks/ips', ipam_subnet: '/networks/subnets',
      virt_hypervisor: '/virt/hypervisors', rack: '/infrastructure/racks',
      database_instance: '/apps/databases',
      instance_database: '/apps/databases',
    };
    return routes[n.ci_type] || null;
  };

  const ciDetailPath = (n) => {
    const type = n && n.ci_type;
    if (type === 'rack_element') return `/api/elements/${n.ci_id}`;
    if (type === 'virt_vm' || type === 'vm') return `/api/virt/vms/${n.ci_id}`;
    if (type === 'firewall') return `/api/firewalls/${n.ci_id}`;
    if (type === 'service') return `/api/services/${n.ci_id}`;
    if (type === 'printer') return `/api/printers/${n.ci_id}`;
    if (type === 'workstation') return `/api/workstations/${n.ci_id}`;
    if (type === 'router') return `/api/routers/${n.ci_id}`;
    if (type === 'ups') return `/api/ups/${n.ci_id}`;
    if (type === 'virt_hypervisor') return `/api/virt/hypervisors/${n.ci_id}`;
    if (type === 'virt_cluster') return `/api/virt/clusters/${n.ci_id}`;
    if (type === 'rack') return `/api/racks/${n.ci_id}`;
    if (type === 'ipam_subnet') return `/api/ipam/subnets/${n.ci_id}`;
    if (type === 'isp_circuit') return `/api/isp/circuits/${n.ci_id}`;
    if (type === 'wifi_controller') return `/api/wifi/controllers/${n.ci_id}`;
    if (type === 'wifi_access_point') return `/api/wifi/aps/${n.ci_id}`;
    if (type === 'wifi_ssid') return `/api/wifi/ssids/${n.ci_id}`;
    if (type === 'database_instance') return `/api/apps/databases/${n.ci_id}`;
    // Una base alojada no tiene endpoint propio: se lee de su instancia.
    if (type === 'instance_database' && n.instance_id) {
      return `/api/apps/databases/${n.instance_id}/databases/${n.ci_id}`;
    }
    return null;
  };

  const openDetail = (n) => {
    if (!n) return;
    setDetailNode(n);
    setDetailInfo(null);
    setDetailError(null);
    setDetailNote(null);
    setDetailIps(null);
    // Los grupos y las tarjetas-resumen no tienen ficha: el panel pinta su
    // listado de miembros o su programación con los datos del propio grafo.
    if (n.is_logical) {
      setDetailLoading(false);
      return;
    }
    const headers = (api.getAuthHeaders && api.getAuthHeaders()) || {};
    // El backend resuelve la empresa desde `?company_id=` y, si no viene, cae a
    // la del token JWT (la empresa "de casa" del usuario), que NO tiene por qué
    // ser la seleccionada en el selector superior. Sin esto, abrir el detalle de
    // un elemento de la empresa seleccionada devuelve 404: el id existe, pero en
    // otra empresa. (Igual que hace el resto de la app pasando `companyId`.)
    const withCompany = (path) => {
      if (!companyId || companyId === 'all') return path;
      return `${path}${path.includes('?') ? '&' : '?'}company_id=${encodeURIComponent(companyId)}`;
    };
    // Las IPs (configuración de red) viven en IPAM, no en la ficha del CI.
    const loadIps = (ciType, ciId) => {
      if (ciId == null || !ciType) return;
      const q = `object_type=${encodeURIComponent(ciType)}&object_id=${ciId}`;
      fetch(withCompany(`/api/ipam/assignments?${q}`), { headers, credentials: 'include' })
        .then((r) => (r.ok ? r.json() : { data: [] }))
        .then((p) => setDetailIps((p.data || []).filter((a) => a.status === 'active')))
        .catch(() => setDetailIps([]));
    };
    const readFicha = async (path) => {
      const r = await fetch(withCompany(path), { headers, credentials: 'include' });
      const p = await r.json().catch(() => ({}));
      return { ok: r.ok, status: r.status, data: r.ok ? (p.data || p) : null };
    };
    const path = ciDetailPath(n);
    if (!path) {
      setDetailLoading(false);
      setDetailNote('Este tipo de elemento no tiene ficha propia: se muestran los datos del mapa.');
      loadIps(n.ci_type, n.ci_id);
      return;
    }
    setDetailLoading(true);
    (async () => {
      try {
        let res = await readFicha(path);
        let usedId = n.ci_id;
        // Plan B: los ids guardados en el grafo pueden quedar desfasados (CI
        // recreado, importado o re-sembrado). Si la ficha da 404, buscamos el
        // elemento por NOMBRE y reintentamos con el id correcto.
        if (!res.ok && res.status === 404 && n.name) {
          try {
            const s = await api.sdSearchCIs(companyId, [n.ci_type], String(n.name).trim(), 10);
            const list = s.data || [];
            const same = String(n.name).trim().toLowerCase();
            const hit = list.find((x) => String(x.name).trim().toLowerCase() === same) || list[0];
            if (hit && hit.ci_id && Number(hit.ci_id) !== Number(n.ci_id)) {
              const alt = ciDetailPath({ ...n, ci_id: hit.ci_id });
              if (alt) {
                const res2 = await readFicha(alt);
                if (res2.ok) {
                  res = res2;
                  usedId = hit.ci_id;
                  setDetailNote(`Ficha recuperada por nombre (#${hit.ci_id}; el grafo apuntaba a #${n.ci_id}).`);
                }
              }
            }
          } catch { /* si la búsqueda falla, mantenemos el error original */ }
        }
        setDetailInfo(res.data);
        if (!res.ok) {
          setDetailError(`No se ha podido cargar la ficha (${n.ci_type} #${n.ci_id}, HTTP ${res.status}).`);
        }
        // Si la ficha ya trae las IPs (VM / hypervisor), se usan esas.
        if (res.data && Array.isArray(res.data.ip_assignments)) {
          setDetailIps(res.data.ip_assignments.filter((a) => a.status === 'active'));
        } else {
          loadIps(n.ci_type, usedId);
        }
      } catch (e) {
        setDetailError(`No se ha podido cargar la ficha: ${e.message || 'error de red'}.`);
      } finally {
        setDetailLoading(false);
      }
    })();
  };
  const closeDetail = () => { setDetailNode(null); setDetailInfo(null); setDetailLoading(false); setDetailError(null); setDetailNote(null); setDetailIps(null); };
  const goDetailModule = () => {
    if (!detailNode) return;
    const r = ciRoute(detailNode);
    if (!r) return;   // nodo derivado sin módulo propio (AD, etc.): nada que abrir
    const sep = r.includes('?') ? '&' : '?';
    navigate(detailNode && detailNode.ci_id != null ? `${r}${sep}destacar=${detailNode.ci_id}` : r);
  };
  // Ir directamente a la ficha del elemento en su módulo (con ?destacar=id)
  const goToElementRecord = (n) => {
    if (!n) return;
    const r = ciRoute(n);
    if (!r) return;   // sin módulo propio (nodos AD, etc.)
    const sep = r.includes('?') ? '&' : '?';
    navigate(n && n.ci_id != null ? `${r}${sep}destacar=${n.ci_id}` : r);
  };

  const loadGraph = useCallback(async (d, { silent = false } = {}) => {
    if (isFocus) {
      // Grafo centrado en el elemento: no hay layout ni visibilidad que guardar.
      setLoading(true);
      setError(null);
      try {
        const res = await api.sdGetCiGraph(focusCi.ciType, focusCi.ciId, companyId, {
          depth: Number(focusDepth) || 1,
          direction: focusDirection,
        });
        setGraph(res.data);
        setService({ display_name: focusCi.name, name: focusCi.name });
        setUserPos({});
        userPosRef.current = {};
        setHiddenIds([]);
        setCollapsedIds([]);
        setHiddenDerived([]);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!serviceId || !companyId) return;
    // Las recargas tras una acción (añadir, quitar, editar…) se hacen en
    // segundo plano: el grafo anterior sigue visible y no aparece el cartel
    // «Cargando grafo…». El spinner solo se usa al entrar o cambiar de servicio.
    if (!silent) setLoading(true);
    setError(null);
    try {
      const res = await api.sdGetGraph(serviceId, companyId, d);
      setGraph(res.data);
      setService(res.data.service);
      // Restaurar posiciones guardadas del usuario (layout persistido)
      const storedPos = (res.data && res.data.positions) || {};
      setUserPos(storedPos);
      userPosRef.current = { ...storedPos };
      // Restaurar los elementos ocultos del servicio
      setHiddenIds(Array.isArray(res.data && res.data.hidden) ? res.data.hidden : []);
      setCollapsedIds(Array.isArray(res.data && res.data.collapsed) ? res.data.collapsed : []);
      setHiddenDerived(
        Array.isArray(res.data && res.data.hidden_derived) ? res.data.hidden_derived : []
      );
    } catch (e) {
      setError(e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [serviceId, companyId, isFocus, focusCi, focusDepth, focusDirection]);

  useEffect(() => {
    loadGraph(depth);
    if (companyId) {
      api.sdListRelationTypes(companyId).then((res) => setRelationTypes(res.data || [])).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, companyId, focusKey, focusDepth, focusDirection]);

  // Cambio de servicio SIN desmontar el componente (para no perder pantalla
  // completa): se limpia el estado temporal de la vista anterior y se vuelve a
  // encajar el grafo nuevo.
  useEffect(() => {
    setSelectedNodeId(null);
    setConnectTarget(null);
    setRelationTypeId('');
    setConnectDirection('a_to_b');
    setConnectBidirectional(false);
    setConnNodeId(null);
    setShowNarrative(false);
    setShowModelApply(false);
    setShowLogicalInput(false);
    setEditRel(null);
    setShowDerivedPanel(false);
    setNotice(null);
    setError(null);
    setDetailNode(null);
    setDetailInfo(null);
    setZoom(1);
    setPan({ x: 0, y: 0 });
    setShowHiddenPanel(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId, focusKey]);

  // Rueda del ratón = zoom sobre el lienzo (listener no pasivo para poder
  // cancelar el scroll por defecto del contenedor).
  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    const onWheel = (e) => {
      e.preventDefault();
      setZoom((z) => Math.min(3, Math.max(0.3, z * (e.deltaY < 0 ? 1.1 : 0.9))));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, []);

  // Etiquetas de tipos para los filtros
  const typeLabel = (t) => (
    t === 'rack_element'
      ? 'Servidores / físico'
      : (CI_TYPE_LABELS[t] || KIND_META[t]?.label || t)
  );

  // Carga de elementos de la empresa según texto y tipos activos.
  // q vacío ⇒ lista los primeros de cada tipo activo (catálogo completo).
  const loadCatalog = useCallback(async (q, hide) => {
    setSearching(true);
    try {
      const types = SEARCHABLE_TYPES.filter((t) => !hide[t]);
      // Límite alto por tipo para que, con los volúmenes habituales de una
      // empresa, aparezcan todos sus elementos (no solo 12).
      const res = await api.sdSearchCIs(companyId, types, (q || '').trim(), 200);
      const rows = (res.data || []).map((r) => ({
        ci_type: r.ci_type, ci_id: r.ci_id, kind: r.kind,
        name: r.name || `${CI_TYPE_LABELS[r.ci_type] || KIND_META[r.ci_type]?.label || r.ci_type} #${r.ci_id}`,
      }));
      setResults(rows);
    } catch (e) {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [companyId]);

  // Cargar el catálogo completo al entrar
  useEffect(() => {
    if (companyId) { loadCatalog('', {}); }
  }, [companyId, loadCatalog]);

  const toggleType = (t) => {
    const hide = { ...hiddenTypes, [t]: !hiddenTypes[t] };
    setHiddenTypes(hide);
    loadCatalog(query, hide);
  };
  const setAllTypes = (show) => {
    const next = show
      ? {}
      : Object.fromEntries(SEARCHABLE_TYPES.map((t) => [t, true]));
    setHiddenTypes(next);
    loadCatalog(query, next);
  };
  const clearCatalogSearch = () => {
    setQuery('');
    setHiddenTypes({});
    loadCatalog('', {});
  };

  const nodeLookup = useMemo(() => {
    const map = {};
    (graph?.nodes || []).forEach((n) => { map[n.id] = n; });
    return map;
  }, [graph]);

  const connNode = connNodeId ? nodeLookup[connNodeId] : null;

  // Nombre legible de un nodo (los nodos derivados o los miembros "sin tipo real"
  // pueden no tener `name`: se cae a su etiqueta de tipo en vez de mostrar el id).
  const nodeDisplayName = (n) => {
    if (!n) return '';
    const m = metaFor(n);
    return n.name || n.logical_label || m.label || String(n.ci_id ?? n.id);
  };

  // Tipos de relación compatibles con los dos extremos seleccionados. Si un
  // tipo limita source_type/target_type, se admite en cualquiera de los dos
  // sentidos (el usuario puede invertir la dirección con ⇄).
  const relationTypesForPair = useCallback((a, b) => {
    if (!a || !b) return relationTypes;
    const fits = (rt, src, tgt) => (
      (!rt.source_type || rt.source_type === src.ci_type)
      && (!rt.target_type || rt.target_type === tgt.ci_type)
    );
    return relationTypes.filter((rt) => fits(rt, a, b) || fits(rt, b, a));
  }, [relationTypes]);
  const compatibleRelationTypes = useMemo(
    () => relationTypesForPair(nodeLookup[selectedNodeId], nodeLookup[connectTarget]),
    [relationTypesForPair, nodeLookup, selectedNodeId, connectTarget]
  );

  // Conexiones tipadas (relations) del nodo, separadas por sentido para que el
  // panel se lea de un vistazo: qué sale de este elemento y qué llega a él.
  const nodeRelations = useMemo(() => {
    if (!connNodeId) return [];
    return (graph?.edges || [])
      .filter((e) => e.kind === 'relation' && (e.source === connNodeId || e.target === connNodeId))
      .map((e) => {
        const dir = e.source === connNodeId ? 'out' : 'in';
        const otherId = dir === 'out' ? e.target : e.source;
        const other = nodeLookup[otherId];
        const cat = edgeCategory(e);
        return {
          relId: Number(String(e.id || '').replace('rel:', '')),
          derived: Boolean(e.derived),
          dir,
          relLabel: e.relation_display || e.relation_type || 'relación',
          catColor: EDGE_CATEGORIES[cat].color,
          catLabel: EDGE_CATEGORIES[cat].label,
          otherId,
          otherName: (other && (other.name || other.logical_label || metaFor(other).label)) || otherId,
          otherMeta: metaFor(other || { ci_type: '?' }),
          otherIsService: Boolean(other && other.ci_type === 'service' && !other.is_root),
          otherCiId: other ? other.ci_id : null,
        };
      });
  }, [connNodeId, graph, nodeLookup]);
  const relsOut = useMemo(() => nodeRelations.filter((r) => r.dir === 'out'), [nodeRelations]);
  const relsIn = useMemo(() => nodeRelations.filter((r) => r.dir === 'in'), [nodeRelations]);

  const isMember = (ciType, ciId) => {
    // Solo bloquea si la CI es miembro DIRECTO de este servicio. Una CI que
    // meramente aparece expandida (parte de un servicio anidado o vía una
    // relación) sigue pudiendo añadirse como miembro directo.
    return nodeIdsForCi(ciType, ciId).some((nid) => {
      const n = nodeLookup[nid];
      return Boolean(n && n.member_id && String(n.service_id) === String(serviceId));
    });
  };

  const addCi = async (ciType, ciId, name, targetGroupId = null) => {
    if (readOnly) {
      setNotice('Vista de sólo lectura: vuelve al servicio para añadir elementos.');
      return;
    }
    if (ciType === 'service' && Number(ciId) === Number(serviceId)) {
      setNotice('Un servicio no puede ser miembro de sí mismo.');
      return;
    }
    if (isMember(ciType, ciId)) {
      setNotice(`${cyLabel(name, 40)} ya está en el servicio.`);
      return;
    }
    try {
      await api.sdAddMember(serviceId, companyId, { ci_type: ciType, ci_id: ciId });
      if (targetGroupId) {
        // Soltado encima de una caja de grupo: además de miembro, se crea la
        // pertenencia `contains` sin obligar a soltarlo primero en el canvas.
        await addNodeToGroup(targetGroupId, {
          id: `${ciType}:${ciId}`,
          ci_type: ciType,
          ci_id: Number(ciId),
          name,
          is_logical: false,
        });
      } else {
        setNotice(`${cyLabel(name, 40)} añadido (CI existente, sin duplicar).`);
        await loadGraph(depth, { silent: true });
      }
      // Mantiene abierto el catálogo: refresca la lista con los filtros actuales.
      await loadCatalog(query, hiddenTypes);
    } catch (e) {
      setError(e.message);
    }
  };

  // ── Drag & drop (spec §7): CI desde la búsqueda → canvas ────────────────
  const onCiDragStart = (e, r) => {
    e.dataTransfer.setData(
      'application/x-sd-ci',
      JSON.stringify({ ci_type: r.ci_type, ci_id: r.ci_id, name: r.name })
    );
    e.dataTransfer.effectAllowed = 'copy';
    setDraggingCi({ ci_type: r.ci_type, ci_id: r.ci_id, name: r.name });
  };

  const onCanvasDragEnter = (e) => {
    if (!e.dataTransfer.types || !e.dataTransfer.types.includes('application/x-sd-ci')) return;
    e.preventDefault();
    dragEnterDepth.current += 1;
    setCanDropCi(true);
  };

  const onCanvasDragOver = (e) => {
    if (!e.dataTransfer.types || !e.dataTransfer.types.includes('application/x-sd-ci')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    setDropGid(groupIdAtPoint(e.clientX, e.clientY));
  };

  const onCanvasDragLeave = () => {
    dragEnterDepth.current = Math.max(0, dragEnterDepth.current - 1);
    if (dragEnterDepth.current === 0) {
      setCanDropCi(false);
      setDropGid(null);
    }
  };

  const onCanvasDrop = async (e) => {
    e.preventDefault();
    dragEnterDepth.current = 0;
    setCanDropCi(false);
    setDraggingCi(null);
    const targetGroupId = groupIdAtPoint(e.clientX, e.clientY);
    setDropGid(null);
    const raw = e.dataTransfer.getData('application/x-sd-ci');
    if (!raw) return;
    try {
      const ci = JSON.parse(raw);
      await addCi(ci.ci_type, ci.ci_id, ci.name, targetGroupId);
    } catch (_) { /* drop malformado → ignorar */ }
  };

  // ── Recolocar un nodo libremente (posición persistida en backend) ────────
  const persistLayout = async () => {
    if (readOnly) return; // el diagrama de un elemento no se persiste
    const valid = new Set((graph?.nodes || []).map((n) => n.id));
    const positions = {};
    Object.entries(userPosRef.current).forEach(([id, p]) => {
      if (valid.has(id) && p && typeof p.x === 'number') positions[id] = { x: p.x, y: p.y };
    });
    if (Object.keys(positions).length === 0) return;
    try {
      await api.sdSaveLayout(serviceId, companyId, positions);
    } catch (_) { /* silencioso: se reintenta en el siguiente arrastre */ }
  };

  // Al arrastrar, el navegador "pinta" selecciones de texto (etiquetas, tarjetas)
  // que ensucian la vista. Se bloquea la selección mientras dura el arrastre.
  const lockTextSelection = () => {
    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
    const sel = window.getSelection && window.getSelection();
    if (sel && sel.removeAllRanges) sel.removeAllRanges();
  };
  const unlockTextSelection = () => {
    document.body.style.userSelect = '';
    document.body.style.webkitUserSelect = '';
  };
  // Red de seguridad: si el ratón se suelta fuera de la ventana, no se queda bloqueado.
  useEffect(() => {
    const onUp = () => unlockTextSelection();
    window.addEventListener('blur', onUp);
    return () => window.removeEventListener('blur', onUp);
  }, []);

  const startNodeMove = (e, nodeId) => {
    if (e.button !== 0) return;
    if (e.target && e.target.closest && (e.target.closest('.sd-node-remove') || e.target.closest('.sd-hide-btn') || e.target.closest('.sd-ungroup'))) return;
    e.preventDefault();
    lockTextSelection();

    // Factor pantalla→contenido: la escala del viewBox (ajuste "meet") por el zoom.
    // Sin esto el nodo no sigue al cursor (se movía de más o de menos).
    let s2c = 1 / (zoomRef.current || 1);
    const svgEl = svgRef.current && svgRef.current.querySelector('svg');
    if (svgEl && svgEl.viewBox && svgEl.viewBox.baseVal) {
      const r = svgEl.getBoundingClientRect();
      const vb = svgEl.viewBox.baseVal;
      if (vb.width && vb.height && r.width && r.height) {
        const vbScale = Math.min(r.width / vb.width, r.height / vb.height);
        if (vbScale > 0) s2c = 1 / (vbScale * (zoomRef.current || 1));
      }
    }

    // Hit-test en coordenadas de PANTALLA (fiable con cualquier zoom/pan): ¿sobre
    // qué caja de grupo está el cursor? Devuelve el id del grupo o null.
    const groupUnderPointer = (cx, cy) => groupIdAtPoint(cx, cy);
    const groupBoxScreenRect = (gid) => {
      const el = Array.from(document.querySelectorAll('.sd-group-box[data-gid]'))
        .find((x) => x.getAttribute('data-gid') === gid);
      return el ? el.getBoundingClientRect() : null;
    };

    // Miembro DENTRO de un grupo: se mueve con coordenadas RELATIVAS a la caja y
    // la caja crece para contenerlo. Si se suelta FUERA de la caja original, sale
    // del grupo (o entra en otro grupo si se suelta encima de él).
    setEscapeHint(false);
    const contained = groupInfo.containedBy[nodeId];
    if (contained) {
      const box = groupBoxesRef.current[contained];
      const cur = layout[nodeId];
      if (!box || !cur) return;
      // La caja crece bajo el cursor; para saber si el usuario quiere SACAR el
      // elemento hay que comparar con la caja que había al empezar, no con la
      // caja ya crecida (si no, el puntero nunca llega a salir por abajo/derecha).
      const originRect = groupBoxScreenRect(contained);
      // Fijar la caja al empezar (si no estaba fijada) para que no se recentre
      // en la jerarquía mientras crece bajo el cursor.
      if (!userPosRef.current[contained]) {
        userPosRef.current = { ...userPosRef.current, [contained]: { x: box.x, y: box.y } };
      }
      const baseX = cur.x - box.x, baseY = cur.y - box.y; // centro relativo actual
      moveDragRef.current = {
        nodeId, startX: e.clientX, startY: e.clientY, baseX, baseY, rel: true, moved: false,
        lastX: e.clientX, lastY: e.clientY, lastAbsX: cur.x, lastAbsY: cur.y,
      };
      setMoveDrag({ nodeId });
      const onMove = (ev) => {
        const d = moveDragRef.current; if (!d) return;
        if (!d.moved && (Math.abs(ev.clientX - d.startX) > 3 || Math.abs(ev.clientY - d.startY) > 3)) d.moved = true;
        d.lastX = ev.clientX; d.lastY = ev.clientY;
        d.lastAbsX = cur.x + (ev.clientX - d.startX) * s2c;
        d.lastAbsY = cur.y + (ev.clientY - d.startY) * s2c;
        // Clamp al borde superior/izquierdo (bajo la cabecera); derecha/abajo la caja crece.
        const nx = Math.max(108, Math.round(d.baseX + (ev.clientX - d.startX) * s2c));
        const ny = Math.max(88, Math.round(d.baseY + (ev.clientY - d.startY) * s2c));
        userPosRef.current = { ...userPosRef.current, [nodeId]: { x: nx, y: ny } };
        setUserPos(userPosRef.current);
        setMoveDrag({ nodeId });
        if (d.moved) {
          const over = groupUnderPointer(ev.clientX, ev.clientY);
          setDropGid(over && over !== contained ? over : null);
          const ESCAPE_MARGIN = 24;
          const outside = originRect && !(
            ev.clientX >= originRect.left - ESCAPE_MARGIN && ev.clientX <= originRect.right + ESCAPE_MARGIN
            && ev.clientY >= originRect.top - ESCAPE_MARGIN && ev.clientY <= originRect.bottom + ESCAPE_MARGIN
          );
          setEscapeHint(Boolean(outside && (!over || over === contained)));
        }
      };
      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        unlockTextSelection();
        const d = moveDragRef.current; moveDragRef.current = null; setMoveDrag(null); setDropGid(null); setEscapeHint(false);
        if (!d || !d.moved) return;
        suppressClickRef.current = true;
        const node = nodeLookup[nodeId];
        // Un pequeño margen evita que un movimiento normal dentro de la caja se
        // interprete como "sacar" solo por rozar el borde.
        const ESCAPE_MARGIN = 24;
        const outsideOrigin = originRect && !(
          d.lastX >= originRect.left - ESCAPE_MARGIN && d.lastX <= originRect.right + ESCAPE_MARGIN
          && d.lastY >= originRect.top - ESCAPE_MARGIN && d.lastY <= originRect.bottom + ESCAPE_MARGIN
        );
        if (node && outsideOrigin) {
          const overGroup = groupUnderPointer(d.lastX, d.lastY);
          if (overGroup && overGroup !== contained && overGroup !== nodeId) {
            addNodeToGroup(overGroup, node);
            return;
          }
          removeNodeFromGroup(contained, node, {
            x: Math.round(d.lastAbsX), y: Math.round(d.lastAbsY),
          });
          return;
        }
        persistLayout();
      };
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      return;
    }

    const base = userPosRef.current[nodeId] || layout[nodeId];
    if (!base) return;
    moveDragRef.current = { nodeId, startX: e.clientX, startY: e.clientY, baseX: base.x, baseY: base.y, moved: false, lastX: e.clientX, lastY: e.clientY };
    setMoveDrag({ nodeId, x: base.x, y: base.y });

    const onMove = (ev) => {
      const d = moveDragRef.current;
      if (!d) return;
      if (!d.moved && (Math.abs(ev.clientX - d.startX) > 3 || Math.abs(ev.clientY - d.startY) > 3)) d.moved = true;
      d.lastX = ev.clientX; d.lastY = ev.clientY;
      const nx = Math.round(d.baseX + (ev.clientX - d.startX) * s2c);
      const ny = Math.round(d.baseY + (ev.clientY - d.startY) * s2c);
      userPosRef.current = { ...userPosRef.current, [nodeId]: { x: nx, y: ny } };
      setUserPos(userPosRef.current);
      setMoveDrag({ nodeId, x: nx, y: ny });
      if (d.moved) setDropGid(groupUnderPointer(ev.clientX, ev.clientY));
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      unlockTextSelection();
      const d = moveDragRef.current;
      moveDragRef.current = null;
      setMoveDrag(null);
      setDropGid(null);
      if (!d || !d.moved) return;
      suppressClickRef.current = true; // un arrastre no debe contar como clic
      const node = nodeLookup[nodeId];
      // ¿Se soltó sobre una caja de grupo? (hit-test en pantalla). Meter es por
      // arrastre; sacar se hace arrastrando fuera de la caja original.
      const overGroup = groupUnderPointer(d.lastX, d.lastY);
      if (node && !node.is_logical && !node.is_root && overGroup && overGroup !== nodeId && !groupInfo.containedBy[nodeId]) {
        addNodeToGroup(overGroup, node);
        return;
      }
      persistLayout();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  const onNodeClick = (nodeId, ev) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    if (!selectedNodeId) {
      setSelectedNodeId(nodeId);
      return;
    }
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null);
      return;
    }
    // En el diagrama de un elemento no se conectan CIs (sólo lectura).
    if (readOnly) { setSelectedNodeId(nodeId); return; }
    // Grupo lógico + elemento:
    //   clic normal → meter el elemento en el grupo (agrupación visual).
    //   Mayús+clic  → abrir el modal para crear una RELACIÓN entre ambos.
    const a = nodeLookup[selectedNodeId];
    const b = nodeLookup[nodeId];
    const relationMode = !!(ev && ev.shiftKey);
    if (a && b && !relationMode) {
      if (a.is_logical && !b.is_logical && !b.is_root) { addNodeToGroup(a.id, b); setSelectedNodeId(null); return; }
      if (b.is_logical && !a.is_logical && !a.is_root) { addNodeToGroup(b.id, a); setSelectedNodeId(null); return; }
    }
    setConnectTarget(nodeId);
  };

  const closeConnectModal = () => {
    setSelectedNodeId(null);
    setConnectTarget(null);
    setRelationTypeId('');
    setConnectDirection('a_to_b');
    setConnectBidirectional(false);
  };

  const connectSelected = async () => {
    if (readOnly) return;
    const src = nodeLookup[selectedNodeId];
    const tgt = nodeLookup[connectTarget];
    if (!src || !tgt || !relationTypeId) return;
    // Dirección elegida en el modal: 'a_to_b' → A es origen ; 'b_to_a' → B es origen
    const [sourceNode, targetNode] = connectDirection === 'a_to_b' ? [src, tgt] : [tgt, src];
    setSaving(true);
    setError(null);
    try {
      await api.sdConnect(serviceId, companyId, {
        relation_type_id: Number(relationTypeId),
        source_ci_type: sourceNode.ci_type,
        source_ci_id: sourceNode.ci_id,
        target_ci_type: targetNode.ci_type,
        target_ci_id: targetNode.ci_id,
        bidirectional: connectBidirectional,
      });
      setNotice(`Relación creada entre ${cyLabel(nodeDisplayName(sourceNode) || sourceNode.ci_id, 24)} y ${cyLabel(nodeDisplayName(targetNode) || targetNode.ci_id, 24)}.`);
      closeConnectModal();
      await loadGraph(depth, { silent: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const openEditRelation = (edge) => {
    if (readOnly) return;
    if (!edge || edge.derived) {
      setNotice('Las relaciones automáticas no se editan: usa «Automáticas» para mostrarlas u ocultarlas.');
      return;
    }
    // Cuando el grupo está plegado la arista se reescribe como `rel:123~g~g`;
    // el id real es la primera parte.
    const relationId = Number(String(edge.id || '').replace('rel:', '').split('~')[0]);
    if (!relationId) {
      setNotice('Esta relación no se puede editar.');
      return;
    }
    const typeId = edge.relation_type_id
      || relationTypes.find((rt) => rt.name === edge.relation_type)?.id
      || '';
    setEditRel({
      relationId,
      sourceId: edge.originalSource || edge.source,
      targetId: edge.originalTarget || edge.target,
      typeId: typeId ? String(typeId) : '',
      swap: false,
      bidirectional: !!edge.bidirectional,
    });
  };
  const closeEditRelation = () => {
    setEditRel(null);
    setEditRelSaving(false);
  };
  const saveEditRelation = async () => {
    if (!editRel || !editRel.typeId) return;
    const baseSource = nodeLookup[editRel.sourceId];
    const baseTarget = nodeLookup[editRel.targetId];
    if (!baseSource || !baseTarget) return;
    const [sourceNode, targetNode] = editRel.swap
      ? [baseTarget, baseSource]
      : [baseSource, baseTarget];
    setEditRelSaving(true);
    setError(null);
    try {
      await api.sdUpdateRelation(editRel.relationId, {
        relation_type_id: Number(editRel.typeId),
        source_ci_type: sourceNode.ci_type,
        source_ci_id: sourceNode.ci_id,
        target_ci_type: targetNode.ci_type,
        target_ci_id: targetNode.ci_id,
        bidirectional: editRel.bidirectional,
      }, companyId);
      setNotice(`Relación actualizada: ${cyLabel(nodeDisplayName(sourceNode), 24)} → ${cyLabel(nodeDisplayName(targetNode), 24)}.`);
      closeEditRelation();
      await loadGraph(depth, { silent: true });
    } catch (e) {
      setError(e.message);
      setEditRelSaving(false);
    }
  };

  const removeNode = async (node) => {
    if (readOnly) return;
    if (!node.member_id) {
      // Only direct members (with member_id) can be removed from the service.
      setNotice('Solo se puede quitar del servicio un elemento que sea miembro directo.');
      return;
    }
    try {
      // Antes de quitar el miembro hay que deshacer sus pertenencias a grupos
      // lógicos abiertos. Si no, quedaría una relación `contains` huérfana
      // apuntando a un CI que ya no está en el servicio.
      const containedBy = groupInfo.containedBy[node.id];
      const containsToRemove = (graph?.edges || []).filter((e) => {
        if (e.relation_type !== 'contains' || !String(e.id || '').startsWith('rel:')) return false;
        if (containedBy && e.source === containedBy && e.target === node.id) return true;
        // El propio nodo puede ser un grupo lógico con miembros dentro.
        return Boolean(node.is_logical) && e.source === node.id;
      });
      for (const edge of containsToRemove) {
        await api.sdDeleteRelation(Number(String(edge.id).replace('rel:', '')), companyId);
      }
      await api.sdRemoveMember(serviceId, node.member_id, companyId);
      setNotice(`"${cyLabel(node.name || node.ci_id, 40)}" eliminado del servicio.`);
      await loadGraph(depth, { silent: true });
    } catch (e) {
      setError(e.message);
    }
  };

  // Componente lógico / agrupador (spec §8): etiqueta sin crear un CI físico.
  // Se representa con un ci_id sintético negativo para no chocar con CIs reales.
  const addLogicalComponent = async () => {
    if (readOnly) return;
    const label = logicalLabel.trim();
    if (!label) { setNotice('Escribe un nombre para el grupo lógico.'); return; }
    let nextId = -1;
    (graph?.nodes || []).forEach((n) => {
      if (n.is_logical && Number(n.ci_id) < nextId) nextId = Number(n.ci_id);
    });
    try {
      await api.sdAddLogicalMember(
        serviceId, companyId, nextId - 1, label, logicalIcon, logicalDetail.trim() || null,
      );
      setNotice(`Grupo lógico "${cyLabel(label, 40)}" añadido (no crea un CI físico).`);
      setLogicalLabel('');
      setLogicalDetail('');
      setLogicalIcon(GROUP_ICONS[0]);
      setShowLogicalInput(false);
      await loadGraph(depth, { silent: true });
    } catch (e) {
      setError(e.message);
    }
  };

  const resetLayout = async () => {
    setUserPos({});
    userPosRef.current = {};
    if (readOnly) {
      setNotice('Disposición restablecida al auto-layout.');
      return;
    }
    try {
      await api.sdSaveLayout(serviceId, companyId, {});
      setNotice('Disposición restablecida al auto-layout.');
    } catch (_) {
      setNotice('Disposición restablecida (no se pudo guardar el vaciado).');
    }
  };

  const toggleFullscreen = () => {
    const el = pageRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      (el.requestFullscreen ? el.requestFullscreen() : Promise.resolve()).catch(() => {});
    } else {
      (document.exitFullscreen ? document.exitFullscreen() : Promise.resolve()).catch(() => {});
    }
  };
  useEffect(() => {
    const onFs = () => setIsFullscreen(Boolean(document.fullscreenElement));
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // Doble clic en un nodo: mostrar sus conexiones para poder quitarlas.
  const openNodeConnections = (nodeId) => {
    setSelectedNodeId(null);
    setConnectTarget(null);
    setRelationTypeId('');
    setConnectDirection('a_to_b');
    setConnNodeId(nodeId);
  };

  const removeRelation = async (relId, label) => {
    if (readOnly) return;
    if (!relId) return;
    try {
      await api.sdDeleteRelation(relId, companyId);
      setNotice(`Conexión "${label}" eliminada.`);
      await loadGraph(depth, { silent: true });
    } catch (e) {
      setError(e.message);
    }
  };

  // ── Pan: arrastrar sobre el lienzo (vacío) para mover el grafo ──────────
  const onCanvasMouseDown = (e) => {
    if (e.target.closest('.sd-node-group')) return;
    if (e.target.closest('.sd-group-box')) return;
    lockTextSelection();
    dragRef.current = { startX: e.clientX, startY: e.clientY, panX: pan.x, panY: pan.y, dragging: false };
  };

  // ── Mover una caja de grupo completa ─────────────────────────────────────
  const startGroupMove = (e, groupId) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    e.preventDefault();
    lockTextSelection();
    const b = groupBoxesRef.current[groupId];
    if (!b) return;
    const startBx = b.x, startBy = b.y;
    const onMove = (ev) => {
      const z = zoomRef.current || 1;
      const dx = (ev.clientX - e.clientX) / z;
      const dy = (ev.clientY - e.clientY) / z;
      userPosRef.current = { ...userPosRef.current, [groupId]: { x: Math.round(startBx + dx), y: Math.round(startBy + dy) } };
      setUserPos({ ...userPosRef.current });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      unlockTextSelection();
      persistLayout();
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  const onCanvasMouseMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dx = e.clientX - d.startX;
    const dy = e.clientY - d.startY;
    if (!d.dragging && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) d.dragging = true;
    if (d.dragging) setPan({ x: d.panX + dx, y: d.panY + dy });
  };
  const endDrag = () => { dragRef.current = null; unlockTextSelection(); };

  const allNodes = useMemo(() => (graph?.nodes || []), [graph]);
  const hiddenSet = useMemo(() => new Set(hiddenIds), [hiddenIds]);
  const collapsedSet = useMemo(() => new Set(collapsedIds), [collapsedIds]);
  const hiddenDerivedSet = useMemo(() => new Set(hiddenDerived), [hiddenDerived]);

  // Miembros de cada grupo lógico (relación `contains`). Se calcula sobre TODOS
  // los nodos/aristas, antes de ocultar, para saber qué hay dentro al plegar.
  const groupMembers = useMemo(() => {
    const byId = {};
    allNodes.forEach((n) => { byId[n.id] = n; });
    const out = {};
    (graph?.edges || []).forEach((e) => {
      const src = byId[e.source];
      const tgt = byId[e.target];
      if (e.relation_type !== 'contains' || !src || !src.is_logical || !tgt) return;
      if (tgt.is_root || tgt.is_logical) return;
      (out[e.source] = out[e.source] || []).push(e.target);
    });
    return out;
  }, [allNodes, graph]);

  // Al plegar un grupo, sus miembros se ocultan y sus aristas se redirigen a la
  // caja del grupo. Así no queda una maraña de líneas ni huecos vacíos.
  const collapsedMembers = useMemo(() => {
    const set = new Set();
    Object.entries(groupMembers).forEach(([gid, members]) => {
      if (collapsedSet.has(gid)) members.forEach((mid) => set.add(mid));
    });
    return set;
  }, [groupMembers, collapsedSet]);
  const effectiveHiddenSet = useMemo(
    () => new Set([...hiddenSet, ...collapsedMembers]),
    [hiddenSet, collapsedMembers]
  );
  const unitForCollapsed = useCallback((id) => {
    for (const [gid, members] of Object.entries(groupMembers)) {
      if (collapsedSet.has(gid) && members.includes(id)) return gid;
    }
    return id;
  }, [groupMembers, collapsedSet]);

  // El grafo solo pinta lo visible. Las aristas que tocan un nodo oculto se van
  // con él (si no, quedarían flechas colgando hacia la nada). Si el nodo está
  // dentro de un grupo plegado, la arista se redirige al grupo.
  const edges = useMemo(() => {
    const out = [];
    const seen = new Set();
    (graph?.edges || []).forEach((e) => {
      if (e.derived && hiddenDerivedSet.has(e.id)) return;
      const src = unitForCollapsed(e.source);
      const tgt = unitForCollapsed(e.target);
      if (effectiveHiddenSet.has(e.source) && src === e.source) return;
      if (effectiveHiddenSet.has(e.target) && tgt === e.target) return;
      if (src === tgt) return;
      // Las relaciones `contains` de un grupo plegado son implícitas.
      if (e.relation_type === 'contains' && groupMembers[e.source] && collapsedSet.has(e.source)) return;
      const key = `${e.relation_type || e.kind}:${src}:${tgt}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push(src === e.source && tgt === e.target
        ? e
        : {
            ...e,
            id: `${e.id}~${src}~${tgt}`,
            source: src,
            target: tgt,
            originalSource: e.originalSource || e.source,
            originalTarget: e.originalTarget || e.target,
          });
    });
    return out;
  }, [graph, effectiveHiddenSet, unitForCollapsed, groupMembers, collapsedSet, hiddenDerivedSet]);
  // Lista completa de aristas automáticas, incluso las ocultas: el panel de
  // «Automáticas» debe poder volver a mostrarlas.
  const derivedEdges = useMemo(() => {
    const nameOf = (id) => {
      const n = nodeLookup[id];
      return n ? (n.name || n.logical_label || n.id) : id;
    };
    return (graph?.edges || [])
      .filter((e) => e.derived)
      .map((e) => ({
        id: e.id,
        label: e.relation_display || e.relation_type || 'relación',
        relation: e.relation_type,
        fromName: nameOf(e.source),
        toName: nameOf(e.target),
      }));
  }, [graph, nodeLookup]);
  const nodes = useMemo(
    () => allNodes.filter((n) => !effectiveHiddenSet.has(n.id)),
    [allNodes, effectiveHiddenSet]
  );

  // ── Ocultar / desocultar elementos del grafo (👁) ────────────────────────
  // La visibilidad es por nodo: ocultar un grupo oculta su caja; si un nodo
  // oculto era miembro de un grupo, desaparece del grafo (y vuelve al mostrarlo).
  const saveHidden = (next) => {
    if (readOnly) return; // la visibilidad del diagrama de un elemento es local
    api.sdSaveLayout(serviceId, companyId, undefined, next)
      .catch(() => { /* silencioso: la vista ya está actualizada */ });
  };
  const saveHiddenDerived = (next) => {
    if (readOnly) return;
    api.sdSaveLayout(serviceId, companyId, undefined, undefined, undefined, next)
      .catch(() => { /* silencioso: la vista ya está actualizada */ });
  };
  const toggleDerived = (edgeId) => {
    setHiddenDerived((prev) => {
      const next = prev.includes(edgeId)
        ? prev.filter((x) => x !== edgeId)
        : [...prev, edgeId];
      saveHiddenDerived(next);
      return next;
    });
  };
  const setAllDerivedVisible = (visible) => {
    const next = visible ? [] : derivedEdges.map((e) => e.id);
    setHiddenDerived(next);
    saveHiddenDerived(next);
  };
  const toggleHidden = (nodeId) => {
    setHiddenIds((prev) => {
      const next = prev.includes(nodeId) ? prev.filter((x) => x !== nodeId) : [...prev, nodeId];
      saveHidden(next);
      return next;
    });
  };
  const showAllHidden = () => {
    setHiddenIds([]);
    saveHidden([]);
  };
  const saveCollapsed = (next) => {
    if (readOnly) return;
    api.sdSaveLayout(serviceId, companyId, undefined, undefined, next)
      .catch(() => { /* silencioso: la vista ya está actualizada */ });
  };
  const toggleCollapsed = (groupId) => {
    setCollapsedIds((prev) => {
      const next = prev.includes(groupId)
        ? prev.filter((x) => x !== groupId)
        : [...prev, groupId];
      saveCollapsed(next);
      return next;
    });
  };
  // Al desplegar un grupo con más de 3 miembros se avisa y se deja elegir
  // cuáles mostrar; el resto se ocultan para que el diagrama no sea un caos.
  const requestExpandGroup = (groupId) => {
    const members = groupMembers[groupId] || [];
    if (!members.length || !collapsedSet.has(groupId) || members.length <= 3) {
      toggleCollapsed(groupId);
      return;
    }
    setExpandPrompt({ groupId, members, selected: [...members] });
  };
  const toggleExpandSelected = (nodeId) => {
    setExpandPrompt((prev) => {
      if (!prev) return prev;
      const selected = prev.selected.includes(nodeId)
        ? prev.selected.filter((x) => x !== nodeId)
        : [...prev.selected, nodeId];
      return { ...prev, selected };
    });
  };
  const confirmExpand = () => {
    if (!expandPrompt) return;
    const { groupId, members, selected } = expandPrompt;
    const chosen = new Set(selected);
    setHiddenIds((prev) => {
      const next = new Set(prev);
      members.forEach((id) => {
        if (chosen.has(id)) next.delete(id);
        else next.add(id);
      });
      const list = Array.from(next);
      saveHidden(list);
      return list;
    });
    setCollapsedIds((prev) => {
      const next = prev.filter((x) => x !== groupId);
      saveCollapsed(next);
      return next;
    });
    setExpandPrompt(null);
  };
  // Ocultar / mostrar por TIPO de CI (consola de visibilidad).
  const hideMany = (ids) => {
    setHiddenIds((prev) => {
      const s = new Set(prev); ids.forEach((id) => s.add(id));
      const next = Array.from(s); saveHidden(next); return next;
    });
  };
  const showMany = (ids) => {
    setHiddenIds((prev) => {
      const drop = new Set(ids);
      const next = prev.filter((x) => !drop.has(x)); saveHidden(next); return next;
    });
  };
  // Agrupa todos los elementos del servicio por tipo, para la consola de
  // visibilidad (ocultar/mostrar por tipo y por elemento). La raíz no se oculta.
  const visibilityGroups = useMemo(() => {
    const map = new Map();
    allNodes.forEach((n) => {
      if (n.is_root) return;
      const meta = metaFor(n);
      const key = meta.label || n.ci_type || '—';
      if (!map.has(key)) map.set(key, { key, meta, nodes: [] });
      map.get(key).nodes.push(n);
    });
    return Array.from(map.values())
      .map((g) => ({
        ...g,
        nodes: g.nodes.slice().sort((a, b) => String(a.name || a.logical_label || a.id).localeCompare(String(b.name || b.logical_label || b.id))),
        hiddenCount: g.nodes.filter((n) => hiddenSet.has(n.id)).length,
      }))
      .sort((a, b) => a.key.localeCompare(b.key));
  }, [allNodes, hiddenSet]);

  // Grupos lógicos como contenedores: qué nodos contiene cada grupo.
  const groupInfo = useMemo(() => {
    const byId = {};
    nodes.forEach((n) => { byId[n.id] = n; });
    const groups = nodes.filter((n) => n.is_logical);
    const contains = {};
    const containedBy = {};
    edges.forEach((e) => {
      if (e.relation_type === 'contains' && byId[e.source] && byId[e.source].is_logical) {
        const tgt = byId[e.target];
        // Un grupo NO puede contener la raíz ni otro grupo (sería un dato
        // incoherente que rompería el layout jerárquico): se ignora.
        if (!tgt || tgt.is_root || tgt.is_logical) return;
        (contains[e.source] = contains[e.source] || []).push(e.target);
        containedBy[e.target] = e.source;
      }
    });
    return { groups, contains, containedBy, byId };
  }, [nodes, edges]);

  // Layout JERÁRQUICO (Sugiyama simplificado): el nivel de cada nodo = distancia
  // a la raíz siguiendo las relaciones (BFS), no su tipo de CI. Así el grafo se
  // lee como un árbol de arriba abajo (raíz → sus dependencias directas → …) y
  // las líneas fluyen en vertical sin cruzarse, igual que un diagrama limpio.
  // Los grupos lógicos se tratan como UNA unidad (su caja) y sus miembros se
  // colocan dentro en rejilla.
  const layoutData = useMemo(() => {
    const CARD_W = 176, CARD_H = 60, HGAP = 48, VGAP = 92, TOP = 60;
    const GPAD = 20, GHEAD = 38, GGAPX = 18, GGAPY = 16;
    const { groups, contains, containedBy } = groupInfo;
    const byId = groupInfo.byId;
    const pos = {};
    const groupBoxes = {};
    const root = nodes.find((n) => n.is_root);

    // Solo los grupos CON miembros se dibujan como caja; un grupo vacío (p. ej.
    // "Usuarios remotos") es un actor lógico y se pinta como una tarjeta normal.
    // Los miembros se colocan por CENTRO RELATIVO a la caja: por defecto en
    // rejilla, pero se pueden arrastrar (userPos[miembro] = centro relativo) y la
    // caja CRECE para contenerlos (nunca se salen; para sacar está el botón).
    const HWm = 88, HHm = 30;              // medio-tamaño de una tarjeta normal
    const MINX = GPAD + HWm; // centro mínimo horizontal (bajo cabecera)
    const memberGroups = groups.filter((g) => (contains[g.id] || []).length > 0);
    const gInfos = memberGroups.map((g) => {
      const mem = contains[g.id] || [];
      const memIds = new Set(mem);
      // La cabecera crece si el grupo lleva línea de detalle (p. ej. el pool DHCP).
      const ghead = GHEAD + (g.detail ? 18 : 0);
      const miny = ghead + GPAD + HHm;
      const hasInternal = edges.some(
        (e) => e.kind !== 'membership' && e.relation_type !== 'contains'
          && memIds.has(e.source) && memIds.has(e.target)
      );
      const cols = hasInternal ? 1 : Math.max(1, Math.min(2, mem.length || 1));
      const vgap = hasInternal ? 78 : GGAPY;
      const relCenters = {};
      mem.forEach((mid, i) => {
        const up = userPos[mid];
        if (up && typeof up.x === 'number') {
          relCenters[mid] = { x: Math.max(MINX, up.x), y: Math.max(miny, up.y) };
        } else {
          const c = i % cols, r = Math.floor(i / cols);
          relCenters[mid] = {
            x: MINX + c * (CARD_W + GGAPX),
            y: miny + r * (CARD_H + vgap),
          };
        }
      });
      let w = GPAD * 2 + CARD_W, h = ghead + GPAD * 2 + CARD_H;
      mem.forEach((mid) => {
        w = Math.max(w, relCenters[mid].x + HWm + GPAD);
        h = Math.max(h, relCenters[mid].y + HHm + GPAD);
      });
      return { g, mem, relCenters, w, h, ghead };
    });
    const gBoxById = {};
    gInfos.forEach((gi) => { gBoxById[gi.g.id] = gi; });

    // Unidades = raíz + cajas de grupo + nodos sueltos (los contenidos viven en
    // su caja; los grupos vacíos cuentan como un nodo suelto más = tarjeta).
    const freeNodes = nodes.filter((n) => !n.is_root && !containedBy[n.id] && !gBoxById[n.id]);
    const unitIds = [root && root.id, ...gInfos.map((gi) => gi.g.id), ...freeNodes.map((n) => n.id)].filter(Boolean);
    const isUnit = new Set(unitIds);
    const unitOf = (id) => (containedBy[id] ? containedBy[id] : id); // miembro → su grupo
    const unitW = (id) => (gBoxById[id] ? gBoxById[id].w : CARD_W);
    const unitH = (id) => (gBoxById[id] ? gBoxById[id].h : CARD_H);

    // Adyacencia entre unidades (relaciones reales, sin pertenencia ni contains).
    const adj = {};
    const addAdj = (a, b) => {
      if (a === b) return;
      (adj[a] = adj[a] || new Set()).add(b);
      (adj[b] = adj[b] || new Set()).add(a);
    };
    edges.forEach((e) => {
      if (e.kind === 'membership') return;
      if (e.relation_type === 'contains' && byId[e.source] && byId[e.source].is_logical) return;
      const a = unitOf(e.source), b = unitOf(e.target);
      if (!isUnit.has(a) || !isUnit.has(b)) return;
      addAdj(a, b);
    });

    // Nivel = distancia BFS a la raíz. Lo que no cuelga de la raíz va al nivel 1.
    const level = {};
    if (root) {
      level[root.id] = 0;
      const q = [root.id];
      while (q.length) {
        const u = q.shift();
        (adj[u] ? Array.from(adj[u]) : []).forEach((v) => {
          if (level[v] === undefined) { level[v] = level[u] + 1; q.push(v); }
        });
      }
    }
    unitIds.forEach((id) => { if (level[id] === undefined) level[id] = (root && id === root.id) ? 0 : 1; });

    const byLvl = {};
    unitIds.forEach((id) => { (byLvl[level[id]] = byLvl[level[id]] || []).push(id); });
    const levels = Object.keys(byLvl).map(Number).sort((a, b) => a - b);

    // Orden dentro de cada nivel por baricentro de sus vecinos (menos cruces).
    const order = {};
    const xs = {};
    levels.forEach((l) => { order[l] = byLvl[l].slice(); order[l].forEach((id, i) => { xs[id] = i; }); });
    for (let pass = 0; pass < 10; pass++) {
      const seq = pass % 2 === 0 ? levels : levels.slice().reverse();
      seq.forEach((l) => {
        order[l].forEach((id) => {
          const ns = adj[id];
          let sum = 0, c = 0;
          if (ns) ns.forEach((v) => { if (xs[v] !== undefined && level[v] !== l) { sum += xs[v]; c++; } });
          if (c) xs[id] = sum / c;
        });
        order[l] = order[l].slice().sort((a, b) => xs[a] - xs[b]);
        order[l].forEach((id, i) => { xs[id] = i; });
      });
    }

    // Ancho del contenido para centrar las filas.
    let contentW = 500;
    levels.forEach((l) => {
      const w = order[l].reduce((a, id) => a + unitW(id), 0) + Math.max(0, order[l].length - 1) * HGAP;
      contentW = Math.max(contentW, w);
    });
    const centerX = contentW / 2 + 80;

    // Y de cada nivel: acumula la altura de la fila anterior (las cajas de grupo
    // son más altas) para que nunca se solapen.
    const levelY = {};
    let y = TOP;
    levels.forEach((l) => {
      levelY[l] = y;
      const maxH = Math.max(CARD_H, ...order[l].map((id) => unitH(id)));
      y += maxH + VGAP;
    });

    // Colocar cada unidad: fila centrada, izquierda→derecha según el orden.
    levels.forEach((l) => {
      const units = order[l];
      const totalW = units.reduce((a, id) => a + unitW(id), 0) + Math.max(0, units.length - 1) * HGAP;
      let x = centerX - totalW / 2;
      const rowY = levelY[l];
      units.forEach((id) => {
        const w = unitW(id);
        const gi = gBoxById[id];
        if (gi) {
          // Caja de grupo (respeta el arrastre manual si existe).
          const ug = userPos[id];
          const bx = ug ? ug.x : x;
          const by = ug && ug.y != null ? ug.y : rowY;
          groupBoxes[id] = { x: bx, y: by, w: gi.w, h: gi.h };
          pos[id] = { x: bx + gi.w / 2, y: by + (gi.ghead || GHEAD) / 2 };
          gi.mem.forEach((mid) => {
            const rc = gi.relCenters[mid];
            pos[mid] = { x: bx + rc.x, y: by + rc.y };
          });
        } else {
          pos[id] = { x: x + w / 2, y: rowY + CARD_H / 2 };
        }
        x += w + HGAP;
      });
    });
    return { pos, groupBoxes };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodes, groupInfo, userPos]);
  const layout = layoutData.pos;
  const groupBoxes = layoutData.groupBoxes;
  const groupBoxesRef = useRef({});
  groupBoxesRef.current = groupBoxes;

  // Nodo raíz = el propio servicio (se dibuja como tarjeta central).
  const rootNode = useMemo(() => nodes.find((n) => n.is_root) || null, [nodes]);
  const rootNodeId = rootNode ? rootNode.id : null;

  // Posición efectiva: los nodos dentro de un grupo ignoran la posición manual.
  const posFor = useCallback(
    (id) => (groupInfo.containedBy[id] ? layout[id] : (userPos[id] || layout[id])),
    [groupInfo, layout, userPos]
  );

  // Ancla de una arista: centro + medios-anchos del extremo. Si el extremo es
  // una CAJA de grupo, usa el tamaño real de la caja (no el de una tarjeta), así
  // la flecha toca el borde de la caja y no queda flotando en su interior.
  const nodeAnchor = useCallback((id) => {
    const b = groupBoxesRef.current[id];
    if (b) return { cx: b.x + b.w / 2, cy: b.y + b.h / 2, hw: b.w / 2, hh: b.h / 2 };
    const p = posFor(id) || { x: 0, y: 0 };
    const n = groupInfo.byId[id];
    const hw = n && n.is_root ? 104 : 88;
    return { cx: p.x, cy: p.y, hw, hh: 30 };
  }, [posFor, groupInfo]);

  // Ancla de una arista = el propio elemento (tarjeta o caja de grupo si el
  // extremo ES el grupo). Las líneas ENTRAN en la caja hasta el elemento, como
  // en un diagrama de bloques: se ve a qué elemento concreto llegan.
  const edgeAnchor = useCallback((id) => nodeAnchor(id), [nodeAnchor]);

  // Límites del contenido para ajustar el viewBox (encaja todo al abrir).
  const contentBounds = useMemo(() => {
    const pts = [];
    nodes.forEach((n) => {
      const p = posFor(n.id);
      if (!p) return;
      // La tarjeta del servicio (raíz) es más ancha que el resto.
      const hw = n.is_root ? 112 : 90;
      pts.push({ x: p.x - hw, y: p.y - 34 }, { x: p.x + hw, y: p.y + 34 });
    });
    Object.values(groupBoxes).forEach((b) => { pts.push({ x: b.x, y: b.y }, { x: b.x + b.w, y: b.y + b.h }); });
    if (!pts.length) return { minX: 0, minY: 0, w: 1400, h: 600 };
    const xs = pts.map((p) => p.x);
    const ys = pts.map((p) => p.y);
    // Ajuste más ceñido que antes (50/70): el diagrama ocupa más pantalla sin
    // dejar un marco vacío tan grande alrededor del contenido.
    const PAD = 26, TOP_PAD = 46;
    const minX = Math.min(...xs) - PAD;
    const minY = Math.min(...ys) - TOP_PAD;
    const w = Math.max(420, Math.max(...xs) - Math.min(...xs) + PAD * 2);
    const h = Math.max(320, Math.max(...ys) - Math.min(...ys) + TOP_PAD + PAD);
    return { minX, minY, w, h };
  }, [nodes, groupBoxes, posFor]);

  // ── Grupos-contenedor: meter/sacar elementos (relación "contiene") ──────
  const findContainsRel = (gid, nid) => {
    const e = (graph?.edges || []).find((x) => x.relation_type === 'contains' && x.source === gid && x.target === nid);
    return e ? Number(String(e.id || '').replace('rel:', '')) : null;
  };
  const deleteContains = async (gid, nid) => {
    const id = findContainsRel(gid, nid);
    if (id) await api.sdDeleteRelation(id, companyId);
  };
  const addNodeToGroup = async (groupNodeId, node) => {
    if (readOnly) return;
    const g = nodeLookup[groupNodeId];
    if (!g || !node || node.is_logical) return;
    // El propio servicio (raíz) no puede ser miembro de un grupo: es el hub del
    // grafo. Tampoco tiene sentido meter un grupo dentro de otro.
    if (node.is_root) { setNotice('El servicio raíz no se puede meter en un grupo.'); return; }
    if (node.id === groupNodeId) return;
    const rt = relationTypes.find((r) => r.name === 'contains');
    if (!rt) { setNotice('Falta el tipo de relación "contiene".'); return; }
    try {
      const prev = groupInfo.containedBy[node.id];
      if (prev && prev !== groupNodeId) await deleteContains(prev, node.id);
      await api.sdConnect(serviceId, companyId, {
        relation_type_id: rt.id,
        source_ci_type: 'logical_component',
        source_ci_id: g.ci_id,
        target_ci_type: node.ci_type,
        target_ci_id: node.ci_id,
      });
      delete userPosRef.current[node.id];
      setUserPos({ ...userPosRef.current });
      setNotice(`«${cyLabel(node.name || node.ci_id, 28)}» añadido al grupo «${cyLabel(g.name || g.logical_label, 22)}».`);
      await loadGraph(depth, { silent: true });
    } catch (e) {
      setError(e.message);
    }
  };
  const removeNodeFromGroup = async (groupNodeId, node, absolutePos = null) => {
    if (readOnly) return;
    try {
      await deleteContains(groupNodeId, node.id);
      // Su posición era RELATIVA a la caja; al salir normalmente se descarta
      // para que el auto-layout la recolque. Si viene de un arrastre, se guarda
      // la posición absoluta donde se soltó para que no salte de sitio.
      delete userPosRef.current[node.id];
      if (absolutePos && typeof absolutePos.x === 'number') {
        userPosRef.current[node.id] = absolutePos;
      }
      setUserPos({ ...userPosRef.current });
      await persistLayout();
      setNotice(`«${cyLabel(node.name || node.logical_label || node.ci_id, 28)}» sacado del grupo.`);
      await loadGraph(depth, { silent: true });
    } catch (e) {
      setError(e.message);
    }
  };

  const narrative = useMemo(
    () => buildNarrative(nodes, edges, service, isFocus ? nodes.find((n) => n.is_focus) : null),
    [nodes, edges, service, isFocus]
  );
  const legendKinds = useMemo(() => {
    const seen = new Map();
    nodes.forEach((n) => {
      const m = metaFor(n);
      if (!seen.has(m.label)) seen.set(m.label, m);
    });
    return Array.from(seen.values());
  }, [nodes]);
  const legendCats = useMemo(() => {
    const seen = new Set();
    edges.forEach((e) => seen.add(edgeCategory(e)));
    return Array.from(seen).map((c) => ({ key: c, ...EDGE_CATEGORIES[c] }));
  }, [edges]);

  // Nodos alcanzables desde la raíz SIGUIENDO relaciones tipadas (BFS no dirigido).
  // Si un miembro NO es alcanzable así (p. ej. un servicio "contenedor" cuya raíz
  // solo tiene pertenencia), se conserva su línea de pertenencia para que la raíz
  // no quede suelta.
  const typedReachable = useMemo(() => {
    const adj = {};
    edges.forEach((e) => {
      if (e.kind === 'membership') return;
      (adj[e.source] = adj[e.source] || []).push(e.target);
      (adj[e.target] = adj[e.target] || []).push(e.source);
    });
    const reach = new Set();
    if (rootNodeId) {
      const q = [rootNodeId];
      reach.add(rootNodeId);
      while (q.length) {
        const u = q.shift();
        (adj[u] || []).forEach((v) => { if (!reach.has(v)) { reach.add(v); q.push(v); } });
      }
    }
    return reach;
  }, [edges, rootNodeId]);

  // Aristas que unen el mismo par de nodos (en cualquier sentido) → índice
  // para separarlas lateralmente y que no se dibujen una encima de otra.
  const parallelInfo = useMemo(() => {
    const counts = {};
    const indexMap = {};
    const seen = {};
    const pairKey = (a, b) => [a, b].sort().join('|');
    edges.forEach((e) => {
      const k = pairKey(e.source, e.target);
      counts[k] = (counts[k] || 0) + 1;
    });
    edges.forEach((e) => {
      const k = pairKey(e.source, e.target);
      indexMap[e.id] = seen[k] || 0;
      seen[k] = (seen[k] || 0) + 1;
    });
    return { counts, indexMap };
  }, [edges]);

  // Etiquetas de relación con anti-solape: si dos caen muy cerca, la segunda baja
  // hasta encontrar hueco (si no, se apilaban y no se leía ninguna). Además no
  // pueden tapar el NOMBRE de una tarjeta: antes solo se apartaban de otras
  // etiquetas y acababan encima de los nodos (ilegibles).
  const edgeLabels = useMemo(() => {
    const out = [];
    const placed = [];
    // Obstáculos = tarjetas (no las cajas de grupo: dentro de una caja la
    // etiqueta sí debe ir dentro, donde están los elementos).
    const obstacles = [];
    nodes.forEach((n) => {
      const p = posFor(n.id);
      if (!p) return;
      const hw = n.is_root ? 112 : 90, hh = 32;
      obstacles.push({ x: p.x - hw, y: p.y - hh, w: hw * 2, h: hh * 2 });
    });
    const hitsCard = (cx, cy, w, h, r) =>
      !(cx + w / 2 <= r.x || r.x + r.w <= cx - w / 2
        || cy + h / 2 <= r.y || r.y + r.h <= cy - h / 2);
    edges.forEach((e) => {
      if (e.kind === 'membership') return;
      const label = e.relation_display || e.relation_type || '';
      if (!label) return;
      if (e.relation_type === 'contains' && groupInfo.byId[e.source] && groupInfo.byId[e.source].is_logical) return;
      const s = posFor(e.source), t = posFor(e.target);
      if (!s || !t) return;
      const k = [e.source, e.target].sort().join('|');
      const total = parallelInfo.counts[k] || 1;
      const idx = parallelInfo.indexMap[e.id] || 0;
      const signedCurve = (e.source < e.target ? 1 : -1) * ((idx - (total - 1) / 2) * 26);
      const A = edgeAnchor(e.source), B = edgeAnchor(e.target);
      const sa = rectBorderPoint(A.cx, A.cy, B.cx, B.cy, A.hw, A.hh);
      const ta = rectBorderPoint(B.cx, B.cy, A.cx, A.cy, B.hw, B.hh);
      const mid = curveMid(sa.x, sa.y, ta.x, ta.y, signedCurve);
      // Dentro de un grupo, la etiqueta no debe tapar las tarjetas de arriba.
      const gidS = groupInfo.containedBy[e.source];
      const intraGroup = !!(gidS && gidS === groupInfo.containedBy[e.target]);
      if (intraGroup) {
        const box = groupBoxes[gidS];
        if (box) { const top = box.y + 38 + 20; if (mid.y < top + 10) mid.y = Math.max(top + 10, (s.y + t.y) / 2); }
      }
      const w = label.length * 6.2 + 18, h = 22;
      // Hueco más cercano (arriba o abajo) libre de etiquetas y de tarjetas.
      const offsets = [0, 26, -26, 52, -52, 78, -78, 104, -104, 130, -130, 156, -156];
      let chosen = null;
      for (const dy of offsets) {
        if (dy < 0 && intraGroup) continue; // dentro de un grupo no subir (cabecera)
        const cy = mid.y + dy;
        if (placed.some((p) => Math.abs(p.x - mid.x) < (p.w + w) / 2 + 6 && Math.abs(p.y - cy) < (p.h + h) / 2 + 3)) continue;
        if (obstacles.some((o) => hitsCard(mid.x, cy, w, h, o))) continue;
        chosen = cy;
        break;
      }
      mid.y = chosen === null ? mid.y + 156 : chosen;
      placed.push({ x: mid.x, y: mid.y, w, h });
      out.push({
        id: e.id, x: mid.x, y: mid.y,
        label: e.bidirectional ? `↔ ${label}` : label,
        edge: e,
        editable: !e.derived && String(e.id || '').startsWith('rel:'),
        catMeta: EDGE_CATEGORIES[edgeCategory(e)],
        isSel: (e.source === selectedNodeId && e.target === connectTarget) || (e.target === selectedNodeId && e.source === connectTarget),
      });
    });
    return out;
  }, [edges, nodes, posFor, groupInfo, groupBoxes, parallelInfo, selectedNodeId, connectTarget, edgeAnchor]);

  return (
    <div className={`sd-graph-page ${isFullscreen ? 'sd-fullscreen' : ''}`} ref={pageRef}>
      {/* Migas de navegación: permite volver al servicio anterior (y saltar a cualquiera). */}
      {isFocus && (
        <nav className="sd-crumbs" aria-label="Navegación">
          <button
            className="sd-back-btn"
            onClick={() => (onExitFocus ? onExitFocus() : onBack && onBack())}
            title={(focusTrail || []).length > 1 ? 'Volver al elemento anterior' : 'Volver al diagrama del servicio'}
          >
            ← {(focusTrail || []).length > 1 ? 'Volver' : 'Volver al servicio'}
          </button>
          <button className="sd-crumb" onClick={() => (onExitFocusAll ? onExitFocusAll() : onBack && onBack())} title="Volver al diagrama del servicio">
            Servicio
          </button>
          {(focusTrail && focusTrail.length ? focusTrail : (focusCi ? [focusCi] : [])).map((f, i, arr) => (
            <span key={`${f.ciType || f.ci_type}:${f.ciId || f.ci_id}:${i}`} className="sd-crumb-seg">
              <span className="sd-crumb-sep">›</span>
              {i === arr.length - 1 ? (
                <span className="sd-crumb sd-crumb-cur" title="Diagrama centrado en este elemento">
                  🔎 {cyLabel(f.name || f.ciId || f.ci_id, 30)}
                </span>
              ) : (
                <button className="sd-crumb" onClick={() => onFocusToIndex && onFocusToIndex(i)} title={`Ver el diagrama de «${f.name}»`}>
                  🔎 {cyLabel(f.name || f.ciId || f.ci_id, 30)}
                </button>
              )}
            </span>
          ))}
        </nav>
      )}
      {!isFocus && Array.isArray(navStack) && navStack.length > 0 && (
        <nav className="sd-crumbs" aria-label="Navegación">
          <button
            className="sd-back-btn"
            onClick={() => onBack && onBack()}
            title={navStack.length > 1 ? 'Volver al servicio anterior' : 'Volver a la lista de servicios'}
          >
            ← {navStack.length > 1 ? 'Volver' : 'Servicios'}
          </button>
          <button className="sd-crumb" onClick={() => onGoToList && onGoToList()} title="Lista de servicios">Catálogo</button>
          {navStack.map((s, i) => (
            <span key={`${s.id}:${i}`} className="sd-crumb-seg">
              <span className="sd-crumb-sep">›</span>
              {i === navStack.length - 1 ? (
                <span className="sd-crumb sd-crumb-cur">{cyLabel(s.name || `Servicio #${s.id}`, 26)}</span>
              ) : (
                <button className="sd-crumb" onClick={() => onNavigateToIndex && onNavigateToIndex(i)} title={`Ir a «${s.name || `Servicio #${s.id}`}»`}>
                  {cyLabel(s.name || `Servicio #${s.id}`, 26)}
                </button>
              )}
            </span>
          ))}
        </nav>
      )}
      <div className="sd-graph-header">
        <h2>
          {isFocus
            ? `🔎 ${focusCi.name || focusKey} · de qué depende y dónde impacta`
            : `🧩 ${service?.display_name || service?.name || '…'}`}
        </h2>
        <div className="sd-graph-controls">
          {isFocus ? (
            <>
              <label className="sd-depth-label">Saltos</label>
              <select
                value={focusDepth}
                onChange={(e) => setFocusDepth(e.target.value)}
                title="Cuántos saltos de relaciones seguir desde este elemento"
              >
                <option value="1">Directos (1 salto)</option>
                <option value="2">2 saltos</option>
                <option value="3">3 saltos</option>
              </select>
              <label className="sd-depth-label">Sentido</label>
              <select
                value={focusDirection}
                onChange={(e) => setFocusDirection(e.target.value)}
                title="Todo, sólo de qué depende, o sólo a quién impacta"
              >
                <option value="both">Depende de + impacta en</option>
                <option value="out">Sólo de qué depende</option>
                <option value="in">Sólo dónde impacta</option>
              </select>
            </>
          ) : (
            <>
              <label className="sd-depth-label">Servicios enlazados</label>
              <select value={depth} onChange={(e) => { setDepth(e.target.value); loadGraph(e.target.value, { silent: true }); }} title="Cuánto expandir los servicios que este servicio referencia">
                <option value="0">Colapsados</option>
                <option value="1">Expandir 1 nivel</option>
                <option value="2">Expandir 2 niveles</option>
                <option value="3">Expandir 3 niveles</option>
                <option value="">Expandir todo</option>
              </select>
              <button
                className={`sd-icon-btn ${showAllMembership ? 'sd-icon-btn-active' : ''}`}
                onClick={() => setShowAllMembership((v) => !v)}
                title={showAllMembership ? 'Ocultar líneas de pertenencia' : 'Mostrar todas las líneas de pertenencia a la raíz'}
              >
                🕸️
              </button>
              {derivedEdges.length > 0 && (
                <button
                  className={`sd-icon-btn sd-model-btn ${hiddenDerived.length ? 'sd-icon-btn-active' : ''}`}
                  onClick={() => setShowDerivedPanel(true)}
                  title="Listar y ocultar las flechas que genera automáticamente el diseñador"
                >
                  🔗
                  <span>Automáticas · {derivedEdges.length}</span>
                </button>
              )}
            </>
          )}
          <button
            className={`sd-icon-btn sd-model-btn ${hiddenIds.length ? 'sd-icon-btn-active' : ''}`}
            onClick={() => setShowHiddenPanel(true)}
            title="Consola de visibilidad: ocultar/mostrar por tipo o por elemento"
          >
            👁️
            <span>{hiddenIds.length ? `Visibilidad · ${hiddenIds.length}` : 'Visibilidad'}</span>
          </button>
          <button className="sd-icon-btn" onClick={() => loadGraph(depth)} title="Recargar grafo">
            <ArrowPathIcon width={18} height={18} />
          </button>
          <button className="sd-icon-btn sd-model-btn" onClick={() => setShowNarrative(true)} title="Ver la explicación narrativa del servicio">
            <DocumentTextIcon width={16} height={16} />
            <span>Explicación</span>
          </button>
          {!isFocus && (
            <button className="sd-icon-btn sd-model-btn" onClick={() => setShowModelApply(true)} title="Aplicar una plantilla al servicio">
              <Squares2X2Icon width={16} height={16} />
              <span>Plantilla</span>
            </button>
          )}
          <div className="sd-zoom-controls">
            <button className="sd-icon-btn" onClick={() => setZoom((z) => Math.min(3, z * 1.25))} title="Acercar">＋</button>
            <button className="sd-icon-btn" onClick={() => setZoom((z) => Math.max(0.3, z / 1.25))} title="Alejar">－</button>
            <button className="sd-icon-btn" onClick={resetLayout} title="Restablecer disposición (auto-layout)">⌂</button>
            <button className="sd-icon-btn" onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }} title="Restablecer zoom">⟲</button>
            <button className="sd-icon-btn" onClick={toggleFullscreen} title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}>
              {isFullscreen ? '🡼' : '⛶'}
            </button>
            <span className="sd-depth-label">{Math.round(zoom * 100)}%</span>
          </div>
        </div>
      </div>

      {isFocus && !loading && (
        <div className="sd-focus-bar">
          <span className="sd-focus-chip sd-focus-chip-out">
            ↑ De qué depende <b>{graph?.counts?.out ?? 0}</b>
          </span>
          <span className="sd-focus-chip sd-focus-chip-in">
            ↓ Dónde impacta <b>{graph?.counts?.in ?? 0}</b>
          </span>
          {(graph?.counts?.deep > 0) && (
            <span className="sd-focus-chip">
              … y {graph.counts.deep} en {Number(focusDepth) > 2 ? 'el resto' : 'el siguiente salto'}
            </span>
          )}
          <span className="sd-focus-hint">
            Doble clic en una tarjeta → sus conexiones. Círculo de la tarjeta → su ficha.
          </span>
        </div>
      )}

      {notice && (
        <div className="sd-notice">
          {notice}
          <button onClick={() => setNotice(null)}><XMarkIcon width={14} height={14} /></button>
        </div>
      )}
      {error && (
        <div className="sd-error-banner">
          {error}
          <button onClick={() => setError(null)}><XMarkIcon width={14} height={14} /></button>
        </div>
      )}

      <div className={`sd-graph-layout ${isFocus ? 'sd-graph-layout-focus' : ''}`}>
        {/* Left: CI search / catálogo de la empresa (no en el diagrama de un elemento) */}
        <aside className={`sd-sidebar ${isFocus ? 'sd-sidebar-hidden' : ''}`}>
          <h3>Elementos de la empresa</h3>
          <div className="sd-search-box">
            <MagnifyingGlassIcon width={16} height={16} />
            <input
              value={query}
              onChange={(e) => { setQuery(e.target.value); loadCatalog(e.target.value, hiddenTypes); }}
              placeholder="Buscar por nombre (o déjalo vacío)…"
            />
            {(query || Object.values(hiddenTypes).some(Boolean)) && (
              <button className="sd-search-clear" onClick={clearCatalogSearch} title="Limpiar filtros">✕</button>
            )}
          </div>

          <div className="sd-type-summary">
            <button type="button" className="sd-type-open" onClick={() => setShowTypeModal(true)}
              title="Elegir qué tipos de elemento se buscan">
              <Squares2X2Icon width={14} height={14} />
              Tipos · {SEARCHABLE_TYPES.filter((t) => !hiddenTypes[t]).length}/{SEARCHABLE_TYPES.length}
            </button>
            <span className="sd-type-summary-text">
              {SEARCHABLE_TYPES.filter((t) => !hiddenTypes[t]).length === SEARCHABLE_TYPES.length
                ? 'Todos'
                : `${SEARCHABLE_TYPES.filter((t) => !hiddenTypes[t]).length} activos`}
            </span>
          </div>

          <div className="sd-results">
            {searching && <div className="sd-hint">Cargando…</div>}
            {results.map((r, i) => {
              const added = isMember(r.ci_type, r.ci_id);
              return (
                <div
                  key={`${r.ci_type}:${r.ci_id}:${i}`}
                  className={`sd-result-row ${added ? 'is-added' : ''} ${draggingCi && draggingCi.ci_type === r.ci_type && draggingCi.ci_id === r.ci_id ? 'sd-dragging' : ''}`}
                  draggable={!added}
                  onDragStart={added ? undefined : (e) => onCiDragStart(e, r)}
                  onDragEnd={() => setDraggingCi(null)}
                  title={added ? 'Ya está en el servicio' : 'Arrastra al canvas o a un grupo, o pulsa ➕ para añadirlo'}
                >
                  <span className={`sd-node-dot`} style={{ background: metaFor(r).color }} />
                  <div className="sd-result-info">
                    <span className="sd-result-name">{cyLabel(r.name, 26)}</span>
                    <span className="sd-result-type">
                      {metaFor(r).label}
                      {added && <em className="sd-added-mark"> · ya en el servicio</em>}
                      {r.delegated && (
                        <em className="sd-deleg" title={`Cedido a esta empresa por ${r.owner_company_name || 'otra empresa'}`}>
                          {' · cedido'}
                          {r.owner_company_name ? ` por ${r.owner_company_name}` : ''}
                        </em>
                      )}
                    </span>
                  </div>
                  <button
                    className={`sd-add-btn ${added ? 'is-added' : ''}`}
                    onClick={() => addCi(r.ci_type, r.ci_id, r.name)}
                    disabled={added}
                    title={added ? 'Ya está en el servicio' : 'Añadir al servicio'}
                  >
                    {added ? <CheckIcon width={16} height={16} /> : <PlusIcon width={16} height={16} />}
                  </button>
                </div>
              );
            })}
            {!searching && results.length === 0 && (
              <div className="sd-hint">No hay elementos que coincidan con los filtros.</div>
            )}
          </div>

          <div className="sd-logical-box">
            {showLogicalInput ? (
              <div className="sd-logical-panel">
                <div className="sd-logical-icons">
                  {GROUP_ICONS.map((ic) => (
                    <button
                      key={ic}
                      type="button"
                      className={`sd-logical-icon ${logicalIcon === ic ? 'is-on' : ''}`}
                      onClick={() => setLogicalIcon(ic)}
                      title={`Icono ${ic}`}
                    >{ic}</button>
                  ))}
                </div>
                <div className="sd-logical-form">
                  <input
                    autoFocus
                    value={logicalLabel}
                    onChange={(e) => setLogicalLabel(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') addLogicalComponent(); if (e.key === 'Escape') { setShowLogicalInput(false); setLogicalLabel(''); setLogicalDetail(''); } }}
                    placeholder="Nombre del grupo (p. ej. Tráfico WiFi)"
                  />
                  <button className="sd-add-btn" onClick={addLogicalComponent} title="Añadir grupo lógico"><PlusIcon width={16} height={16} /></button>
                </div>
                <input
                  className="sd-logical-detail"
                  value={logicalDetail}
                  onChange={(e) => setLogicalDetail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') addLogicalComponent(); }}
                  placeholder="Detalle opcional (p. ej. DHCP 10.0.0.100-200)"
                />
              </div>
            ) : (
              <button className="sd-logical-toggle" onClick={() => setShowLogicalInput(true)} title="Crear un agrupador lógico sin CI físico">
                <PlusIcon width={14} height={14} /> Grupo lógico
              </button>
            )}
          </div>

          <details className="sd-help">
            <summary>🛈 Ayuda rápida</summary>
            <p><strong>Añadir elementos:</strong> arrastra un resultado al canvas o pulsa ➕.</p>
            <p><strong>Mover:</strong> arrastra cualquier nodo a la posición que quieras (se guarda).</p>
            <p><strong>Grupos:</strong> crea un grupo y arrastra elementos dentro de su caja (o clic en el grupo y luego en el elemento). Arrastra un miembro fuera de la caja para sacarlo (o pulsa ⇤ sacar).</p>
            <p><strong>Relacionar un grupo:</strong> selecciona el grupo y haz <b>Mayús + clic</b> en el otro elemento. El clic normal lo mete dentro del grupo.</p>
            <p><strong>Cómo conectar:</strong></p>
            <ol>
              <li>Haz clic en un nodo del canvas (origen).</li>
              <li>Haz clic en otro nodo (destino).</li>
              <li>Elige el tipo de relación y pulsa Guardar.</li>
            </ol>
            <p>Los elementos se añaden desde la CMDB existente: nunca se crean duplicados.</p>
          </details>
        </aside>

        {/* Canvas */}
        <main
          className={`sd-canvas ${canDropCi ? 'sd-canvas-droppable' : ''}`}
          ref={svgRef}
          onMouseDown={onCanvasMouseDown}
          onMouseMove={onCanvasMouseMove}
          onMouseUp={endDrag}
          onMouseLeave={endDrag}
          onDragEnter={onCanvasDragEnter}
          onDragOver={onCanvasDragOver}
          onDragLeave={onCanvasDragLeave}
          onDrop={onCanvasDrop}
        >
          {loading ? (
            <div className="sd-canvas-empty">Cargando grafo…</div>
          ) : (
            <svg
              viewBox={`${contentBounds.minX} ${contentBounds.minY} ${contentBounds.w} ${contentBounds.h}`}
              className="sd-svg"
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                {Object.entries(EDGE_CATEGORIES).map(([key, cat]) => (
                  <marker key={key} id={`sd-arrow-${key}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M 0 0 L 10 5 L 0 10 z" fill={cat.color} />
                  </marker>
                ))}
              </defs>
              <g transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
              {/* 1. Marco del servicio — fondo, dibujado primero */}
              {(() => {
                if (!rootNode) return null;
                // El diagrama de un elemento no tiene "marco de servicio".
                if (isFocus) return null;
                const meta = metaFor(rootNode);
                const cPts = [];
                // El propio servicio entra en el marco: su tarjeta se dibuja
                // dentro, así que el borde la tiene que envolver.
                nodes.forEach((n) => {
                  const p = posFor(n.id);
                  if (!p) return;
                  const hw = n.is_root ? 112 : 92;
                  cPts.push([p.x - hw, p.y - 34], [p.x + hw, p.y + 34]);
                });
                Object.values(groupBoxes).forEach((b) => { cPts.push([b.x, b.y], [b.x + b.w, b.y + b.h]); });
                if (!cPts.length) return null;
                const xs = cPts.map((p) => p[0]), ys = cPts.map((p) => p[1]);
                const PAD = 36, THEAD = 40;
                const fx = Math.min(...xs) - PAD;
                const fy = Math.min(...ys) - THEAD;
                const fw = Math.max(...xs) - Math.min(...xs) + PAD * 2;
                const fh = Math.max(...ys) - Math.min(...ys) + THEAD + PAD;
                const isSelRoot = rootNode.id === selectedNodeId;
                return (
                  <g key="svc-frame" className="sd-svc-frame" onClick={(ev) => onNodeClick(rootNode.id, ev)}>
                    <rect x={fx} y={fy} width={fw} height={fh} rx={20}
                      className={`sd-frame-rect ${isSelRoot ? 'is-sel' : ''}`}
                      stroke={meta.color} strokeWidth={2} strokeOpacity={0.45}
                      fill={`color-mix(in srgb, ${meta.color} 4%, var(--bg-canvas, #080b13))`} />
                    <circle cx={fx + fw} cy={fy + fh / 2} r={7} fill={meta.color} opacity={0.6} className="sd-frame-port" />
                  </g>
                );
              })()}
              {/* 2. Cajas de grupo — sobre el fondo */}
              {groupInfo.groups.map((g) => {
                const b = groupBoxes[g.id];
                if (!b) return null;
                const meta = metaFor(g);
                const isSel = g.id === selectedNodeId;
                const isDrop = g.id === dropGid;
                return (
                  <g key={`box-${g.id}`} data-gid={g.id} className={`sd-group-box ${isDrop ? 'is-droptarget' : ''}`} onClick={(ev) => onNodeClick(g.id, ev)} onMouseDown={(e) => startGroupMove(e, g.id)}>
                    <title>{`${g.name || g.logical_label || 'Grupo'}\nGrupo lógico${(groupInfo.contains[g.id] || []).length ? ` · ${(groupInfo.contains[g.id] || []).length} elemento(s)` : ' · vacío'}${g.detail ? `\n${g.detail}` : ''}`}</title>
                    <rect x={b.x} y={b.y} width={b.w} height={b.h} rx={16}
                      className={`sd-group-rect ${isSel ? 'is-sel' : ''} ${isDrop ? 'is-droptarget' : ''}`} stroke={meta.color}
                      fill={`color-mix(in srgb, ${meta.color} 8%, var(--bg-surface, #12141f))`} />
                    {isDrop && (
                      <text x={b.x + b.w / 2} y={b.y + b.h / 2 + 4} textAnchor="middle" className="sd-drop-hint">＋ soltar aquí</text>
                    )}
                    <text
                      x={b.x + 16} y={b.y + 24} className="sd-group-title" fill={meta.color}
                      style={{ fontSize: fitCardFontSize(g.name || g.logical_label || 'Grupo', 12.5, 22, 9) }}
                    >
                      {g.icon || meta.icon} {g.name || g.logical_label || 'Grupo'}
                    </text>
                    {g.detail && (
                      <text x={b.x + 16} y={b.y + 42} className="sd-group-detail" fill={meta.color}>
                        {g.detail.length > 64 ? `${g.detail.slice(0, 62)}…` : g.detail}
                      </text>
                    )}
                    <g
                      className="sd-hide-btn"
                      transform={`translate(${b.x + b.w - (g.member_id ? 34 : 16)}, ${b.y + 16})`}
                      onMouseDown={(ev) => ev.stopPropagation()}
                      onClick={(ev) => { ev.stopPropagation(); toggleHidden(g.id); }}
                    >
                      <title>Ocultar este grupo</title>
                      <rect x={-9} y={-7} width={18} height={14} rx={4} fill="transparent" />
                      <path d="M -5 0 Q 0 -3.6 5 0 Q 0 3.6 -5 0 Z" className="sd-hide-ic" />
                      <circle r={1.3} className="sd-hide-ic-dot" />
                      <line x1={-5.5} y1={4.5} x2={5.5} y2={-4.5} className="sd-hide-ic-slash" />
                    </g>
                    <g
                      className="sd-group-collapse"
                      transform={`translate(${b.x + b.w - (g.member_id ? 56 : 38)}, ${b.y + 16})`}
                      onMouseDown={(ev) => ev.stopPropagation()}
                      onClick={(ev) => { ev.stopPropagation(); if (collapsedSet.has(g.id)) requestExpandGroup(g.id); else toggleCollapsed(g.id); }}
                    >
                      <title>{collapsedSet.has(g.id) ? 'Desplegar el grupo' : 'Plegar el grupo (resumen)'}</title>
                      <rect x={-9} y={-7} width={18} height={14} rx={4} fill="transparent" />
                      <text x={0} y={4} textAnchor="middle" style={{ fontSize: 13, fill: meta.color }}>▾</text>
                    </g>
                    {g.member_id && (
                      <text x={b.x + b.w - 12} y={b.y + 24} textAnchor="end" className="sd-group-remove"
                        onClick={(ev) => { ev.stopPropagation(); removeNode(g); }} title="Eliminar grupo">✕</text>
                    )}
                  </g>
                );
              })}
              {/* 3. Aristas */}
              {edges.map((e) => {
                const s = posFor(e.source);
                const t = posFor(e.target);
                if (!s || !t) return null;
                const isMembership = e.kind === 'membership';
                // Pertenencia raíz→miembro: se oculta si es redundante (el miembro
                // ya cuelga por una relación tipada, vive dentro de una caja, o es
                // un nodo derivado). Si NO, se conserva para que la raíz de un
                // servicio "contenedor" no quede suelta.
                if (isMembership && !showAllMembership) {
                  if (typedReachable.has(e.target)) return null;
                  if (groupInfo.containedBy[e.target]) return null;
                  if (groupInfo.byId[e.target] && groupInfo.byId[e.target].derived) return null;
                }
                // Las relaciones "contiene" de un grupo son implícitas (la caja
                // ya envuelve a sus miembros) → no dibujar la línea.
                if (e.relation_type === 'contains' && groupInfo.byId[e.source] && groupInfo.byId[e.source].is_logical) return null;
                const isSelected = (e.source === selectedNodeId && e.target === connectTarget) ||
                                   (e.target === selectedNodeId && e.source === connectTarget);
                const cat = edgeCategory(e);
                const catMeta = EDGE_CATEGORIES[cat];
                const k = [e.source, e.target].sort().join('|');
                const total = parallelInfo.counts[k] || 1;
                const idx = parallelInfo.indexMap[e.id] || 0;
                const curve = (idx - (total - 1) / 2) * 26;
                const signedCurve = (e.source < e.target ? 1 : -1) * curve;
                const A = edgeAnchor(e.source), B = edgeAnchor(e.target);
                const sa = rectBorderPoint(A.cx, A.cy, B.cx, B.cy, A.hw, A.hh);
                const ta = rectBorderPoint(B.cx, B.cy, A.cx, A.cy, B.hw, B.hh);
                const d = edgePath(sa.x, sa.y, ta.x, ta.y, signedCurve);
                return (
                  <g key={e.id} className={`sd-edge-g ${isSelected ? 'is-selected' : ''}`}>
                    <path
                      d={d}
                      fill="none"
                      stroke={isSelected ? '#f59e0b' : catMeta.color}
                      strokeWidth={isSelected ? 3.2 : 2}
                      strokeDasharray={catMeta.dashed ? '6 5' : undefined}
                      strokeOpacity={isMembership ? 0.55 : 0.9}
                      markerEnd={`url(#sd-arrow-${cat})`}
                      markerStart={e.bidirectional ? `url(#sd-arrow-${cat})` : undefined}
                    />
                    {/* Las etiquetas se dibujan en una pasada aparte, DESPUÉS de
                        las tarjetas, para que nunca queden tapadas por los nodos. */}
                  </g>
                );
              })}
              {/* 4. Tarjetas de nodo */}
              {nodes.map((n) => {
                if (n.is_logical && groupBoxes[n.id]) return null; // grupos CON miembros → caja
                const p = posFor(n.id);
                if (!p) return null;
                const meta = metaFor(n);
                const isSelected = n.id === selectedNodeId;
                const isTarget = n.id === connectTarget;
                const dragging = moveDrag && moveDrag.nodeId === n.id;
                const isRoot = !!n.is_root;
                const isLinkedService = n.ci_type === 'service' && !isRoot;
                // Nombre COMPLETO (el clip de la tarjeta lo recorta si no cabe) +
                // tooltip con todo lo necesario para identificarlo sin abrir nada.
                const fullName = n.name || n.logical_label || String(n.ci_id ?? n.id);
                const cardTip = [
                  fullName,
                  `${meta.label}${n.role ? ` · ${n.role}` : ''}`,
                  n.detail || null,
                  n.ci_type === 'database_instance' && n.db_count
                    ? `${n.db_count} base(s) de datos dentro`
                    : null,
                  n.ci_type !== 'logical_component' && n.ci_id != null ? `CI ${n.ci_type} #${n.ci_id}` : null,
                ].filter(Boolean).join('\n');
                // Una subred ocupa la tarjeta a dos líneas: el CIDR arriba y el
                // NOMBRE debajo (con la VLAN en una etiqueta), porque el nombre
                // compuesto "cidr · nombre · VLAN" no cabe y se quedaba en
                // "10.60.5.0/24 · S…".
                const isSubnet = n.ci_type === 'ipam_subnet';
                const subnetTitle = isSubnet && n.subnet_cidr ? n.subnet_cidr : fullName;
                const HW = isRoot ? 104 : 88;
                let second = meta.label;
                if (isRoot) {
                  const ver = n.software_version ? ` · v${n.software_version}` : '';
                  second = `${meta.label}${ver}`;
                }
                else if (isLinkedService) second = 'Servicio enlazado ↗';
                else if (n.is_logical && n.detail) second = cyLabel(n.detail, 28);
                else if (isSubnet && n.subnet_name) second = cyLabel(n.subnet_name, 24);
                else {
                  second = n.role ? cyLabel(n.role, 22) : meta.label;
                  // Una instancia de BD lleva dentro sus bases: se ve el nº.
                  if (n.ci_type === 'database_instance' && n.db_count) second += ` · ${n.db_count} BD`;
                }
                const rx = 13;
                // Path del acento izquierdo con esquinas correctas (evita el seam recto-redondeado)
                const accentW = 7;
                const accentPath = `M${-HW + rx},${-30} Q${-HW},${-30} ${-HW},${-30 + rx} L${-HW},${30 - rx} Q${-HW},${30} ${-HW + rx},${30} L${-HW + accentW},${30} L${-HW + accentW},${-30} Z`;
                const clipId = `cl-${String(n.id).replace(/[^A-Za-z0-9_-]/g, '_')}`;
                // En el diagrama de un elemento, el borde dice el SENTIDO: verde =
                // de qué depende, naranja = dónde impacta (el resto, su color de tipo).
                const dirStroke = isFocus
                  ? ({ out: '#22c55e', in: '#f97316' }[n.dir] || meta.color)
                  : meta.color;
                return (
                  <g
                    key={n.id}
                    data-gid={n.is_logical ? n.id : undefined}
                    transform={`translate(${p.x}, ${p.y})`}
                    className={`sd-node-group ${isSelected || dragging ? 'sd-node-selected' : ''} ${isTarget ? 'sd-node-target' : ''} ${dropGid === n.id ? 'is-droptarget' : ''} ${isLinkedService ? 'sd-node-linked' : ''} ${isRoot ? 'sd-node-root' : ''}`}
                    onClick={(ev) => onNodeClick(n.id, ev)}
                    onDoubleClick={() => {
                      if (n.is_logical && groupMembers[n.id] && collapsedSet.has(n.id)) {
                        requestExpandGroup(n.id);
                        return;
                      }
                      return isLinkedService && onOpenService ? onOpenService(n.ci_id, n) : openNodeConnections(n.id);
                    }}
                    onMouseDown={(e) => startNodeMove(e, n.id)}
                  >
                    <title>{cardTip}</title>
                    <rect className="sd-card" x={-HW} y={-30} width={HW * 2} height={60} rx={rx}
                      stroke={dirStroke} strokeDasharray={isLinkedService ? '5 4' : undefined} />
                    <path d={accentPath} fill={meta.color} />
                    <clipPath id={clipId}>
                      <rect x={-HW + 54} y={-27} width={HW * 2 - 72} height={54} rx={7} />
                    </clipPath>
                    <circle
                      cx={-HW + 34} cy={0} r={15} fill={meta.color} className="sd-card-badge sd-card-badge-detail"
                      onMouseDown={(ev) => ev.stopPropagation()}
                      onClick={(ev) => { ev.stopPropagation(); openDetail(n); }}
                      title="Ver detalle / conexiones de este elemento"
                    />
                    <text x={-HW + 34} y={6} textAnchor="middle" className="sd-card-icon">{meta.icon}</text>
                    <g clipPath={`url(#${clipId})`}>
                      {/* El nombre lleva a la FICHA; al arrastrar NO navega (se mueve la tarjeta) */}
                      <text
                        x={-HW + 56} y={-6} className="sd-card-name sd-card-name-link"
                        style={{ fontSize: fitCardFontSize(subnetTitle, isRoot ? 15 : 13, isRoot ? 20 : 14, 9.5) }}
                        onClick={(ev) => { ev.stopPropagation(); if (suppressClickRef.current) { suppressClickRef.current = false; return; } goToElementRecord(n); }}
                        title="Abrir la ficha de este elemento en CMDB"
                      >{subnetTitle}</text>
                      <text x={-HW + 56} y={13} className={`sd-card-sub ${isLinkedService ? 'sd-card-sub-link' : ''}`}>{second}</text>
                    </g>
                    {/* La VLAN va en una etiqueta compacta: en la línea del nombre
                        no entra (la tarjeta tiene unos 16 caracteres útiles). */}
                    {n.vlan_label && (
                      <g className="sd-vlan-tag" transform={`translate(${HW - 40}, 22)`}>
                        <rect
                          x={-(n.vlan_label.length * 2.9 + 7)} y={-6}
                          width={n.vlan_label.length * 5.8 + 14} height={12} rx={6}
                          className="sd-vlan-tag-bg"
                        />
                        <text x={0} y={3} textAnchor="middle" className="sd-vlan-tag-txt">{n.vlan_label}</text>
                      </g>
                    )}
                    {/* El círculo/icono de la tarjeta abre el detalle; el nombre navega a la ficha */}
                    {isLinkedService && onOpenService && (
                      <text x={HW - 10} y={24} textAnchor="end" className="sd-card-open" onClick={(ev) => { ev.stopPropagation(); onOpenService(n.ci_id, n); }}>
                        abrir ↗
                      </text>
                    )}
                    {n.is_logical && groupMembers[n.id] && collapsedSet.has(n.id) && (
                      <text
                        x={HW - 10} y={24} textAnchor="end" className="sd-card-open"
                        onMouseDown={(ev) => ev.stopPropagation()}
                        onClick={(ev) => { ev.stopPropagation(); requestExpandGroup(n.id); }}
                        title={`Desplegar ${groupMembers[n.id].length} elemento(s)`}
                      >
                        ▸ abrir
                      </text>
                    )}
                    {!isRoot && (
                      <g
                        className="sd-hide-btn"
                        transform={`translate(${HW - (n.member_id ? 30 : 14)}, -16)`}
                        onMouseDown={(ev) => ev.stopPropagation()}
                        onClick={(ev) => { ev.stopPropagation(); toggleHidden(n.id); }}
                      >
                        <title>Ocultar del grafo</title>
                        <rect x={-9} y={-7} width={18} height={14} rx={4} fill="transparent" />
                        <path d="M -5 0 Q 0 -3.6 5 0 Q 0 3.6 -5 0 Z" className="sd-hide-ic" />
                        <circle r={1.3} className="sd-hide-ic-dot" />
                        <line x1={-5.5} y1={4.5} x2={5.5} y2={-4.5} className="sd-hide-ic-slash" />
                      </g>
                    )}
                    {n.member_id && (
                      <text x={HW - 10} y={-15} textAnchor="end" className="sd-node-remove" onClick={(ev) => { ev.stopPropagation(); removeNode(n); }}>
                        ✕
                      </text>
                    )}
                    {/* Miembro dentro de una caja de grupo → botón VISIBLE para sacarlo. */}
                    {groupInfo.containedBy[n.id] && (
                      <g
                        className="sd-ungroup"
                        transform="translate(0, 30)"
                        onMouseDown={(ev) => ev.stopPropagation()}
                        onClick={(ev) => { ev.stopPropagation(); removeNodeFromGroup(groupInfo.containedBy[n.id], n); }}
                      >
                        <title>Sacar «{n.name || n.ci_id}» del grupo</title>
                        <rect x={-38} y={-9} width={76} height={18} rx={9} className="sd-ungroup-pill" />
                        <text x={0} y={4} textAnchor="middle" className="sd-ungroup-text">⇤ sacar</text>
                      </g>
                    )}
                  </g>
                );
              })}
              {/* 5. Etiquetas de relación — pasada sobre los nodos (legibles siempre).
                  Posiciones precalculadas con anti-solape (edgeLabels). */}
              {edgeLabels.map((L) => {
                const label = L.label;
                const catMeta = L.catMeta;
                const isSelLabel = L.isSel;
                const tw = label.length * 6.2 + 18;
                return (
                  <g
                    key={`lab-${L.id}`}
                    className={`sd-edge-label-g ${isSelLabel ? 'is-selected' : ''} ${L.editable ? 'is-editable' : ''}`}
                    transform={`translate(${L.x}, ${L.y})`}
                    onClick={L.editable ? (ev) => { ev.stopPropagation(); openEditRelation(L.edge); } : undefined}
                    title={L.editable ? 'Clic para cambiar el tipo o el sentido de esta relación' : undefined}
                  >
                    <rect
                      x={-(label.length * 3.1 + 9)} y={-11}
                      width={tw} height={22} rx={11}
                      className="sd-edge-label-bg"
                      fill={isSelLabel ? 'var(--accent, #f59e0b)' : 'var(--bg-surface, #0d0f1a)'}
                      stroke={isSelLabel ? '#f59e0b' : catMeta.color}
                      strokeOpacity={isSelLabel ? 1 : 0.55}
                    />
                    <text textAnchor="middle" y={4} className={`sd-edge-label ${isSelLabel ? 'on-selected' : ''}`} fill={isSelLabel ? '#0d0f1a' : catMeta.color}>{label}</text>
                  </g>
                );
              })}
              </g>
            </svg>
          )}
          {escapeHint && (
            <div className="sd-escape-hint">Suelta para sacar del grupo</div>
          )}
          {!loading && !readOnly && selectedNodeId && nodeLookup[selectedNodeId]?.is_logical && (
            <div className="sd-group-tip">
              <strong>{nodeLookup[selectedNodeId].icon || '🗂️'} {nodeDisplayName(nodeLookup[selectedNodeId])}</strong>
              <span>Clic en un elemento = añadirlo al grupo</span>
              <span><b>Mayús + clic</b> = crear una relación</span>
              <span>Arrastra un miembro fuera = sacarlo</span>
            </div>
          )}
          {!loading && showLegend && (legendKinds.length > 0 || legendCats.length > 0) && (
            <div className="sd-legend">
              <div className="sd-legend-head">
                <span>Leyenda</span>
                <button onClick={() => setShowLegend(false)} title="Ocultar leyenda">✕</button>
              </div>
              {legendKinds.length > 0 && (
                <div className="sd-legend-block">
                  <p className="sd-legend-title">Elementos</p>
                  <div className="sd-legend-items">
                    {legendKinds.map((m) => (
                      <span key={m.label} className="sd-legend-item">
                        <span className="sd-legend-icon" style={{ background: m.color }}>{m.icon}</span>
                        {m.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {legendCats.length > 0 && (
                <div className="sd-legend-block">
                  <p className="sd-legend-title">Conexiones</p>
                  <div className="sd-legend-items">
                    {legendCats.map((c) => (
                      <span key={c.key} className="sd-legend-item">
                        <span className="sd-legend-line" style={{ background: c.color, borderBottom: c.dashed ? `2px dashed ${c.color}` : 'none' }} />
                        {c.label}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {!loading && !showLegend && (
            <button className="sd-legend-show" onClick={() => setShowLegend(true)} title="Mostrar leyenda">Leyenda</button>
          )}
        </main>
      </div>

      {/* Connect modal */}
      {connectTarget && selectedNodeId && (
        <div className="sd-modal-backdrop" onClick={closeConnectModal}>
          <div className="sd-modal" onClick={(e) => e.stopPropagation()}>
            <h3>¿Qué relación existe?</h3>
            <p className="sd-direction-caption-hint">Se lee: <strong>origen — relación — destino</strong>. Usa ⇄ para invertir el sentido.</p>
            <div className="sd-direction-row">
              <span className="sd-direction-name">{cyLabel(nodeDisplayName(nodeLookup[selectedNodeId]) || selectedNodeId, 24)}</span>
              <button
                type="button"
                className="sd-swap-btn"
                onClick={() => setConnectDirection((d) => (d === 'a_to_b' ? 'b_to_a' : 'a_to_b'))}
                title="Invertir dirección"
              >
                ⇄
              </button>
              <span className="sd-direction-name">{cyLabel(nodeDisplayName(nodeLookup[connectTarget]) || connectTarget, 24)}</span>
            </div>
            <p className="sd-direction-caption">
              {(() => {
                const rt = relationTypes.find((x) => x.id === Number(relationTypeId));
                const rel = rt?.display_name || '…';
                const aName = cyLabel(nodeDisplayName(nodeLookup[selectedNodeId]) || selectedNodeId, 30);
                const bName = cyLabel(nodeDisplayName(nodeLookup[connectTarget]) || connectTarget, 30);
                return connectDirection === 'a_to_b' ? `${aName} ${rel} ${bName}` : `${bName} ${rel} ${aName}`;
              })()}
            </p>
            <select value={relationTypeId} onChange={(e) => setRelationTypeId(e.target.value)}>
              <option value="">— Elige tipo de relación —</option>
              {compatibleRelationTypes.map((rt) => (
                <option key={rt.id} value={rt.id}>{rt.display_name} ({rt.name})</option>
              ))}
            </select>
            {compatibleRelationTypes.length === 0 && (
              <p className="sd-rel-help">
                No hay tipos de relación compatibles con estos dos elementos. Revisa el catálogo
                de tipos (por ejemplo, si limita el tipo de origen o destino).
              </p>
            )}
            {(() => {
              const rt = relationTypes.find((x) => x.id === Number(relationTypeId));
              if (!rt) return null;
              const help = RELATION_HELP[rt.name];
              const bits = [`${rt.display_name || rt.name}`, rt.cardinality ? `cardinalidad ${rt.cardinality}` : null];
              if (help) bits.push(` ${help}`);
              if (rt.description) bits.push(rt.description);
              if (rt.inverse_name) bits.push(`Inversa: ${rt.inverse_name}`);
              return <p className="sd-rel-help">{bits.filter(Boolean).join(' · ')}</p>;
            })()}
            <label className="sd-bidirect-row">
              <input
                type="checkbox"
                checked={connectBidirectional}
                onChange={(e) => setConnectBidirectional(e.target.checked)}
              />
              <span>Flecha doble (ida y vuelta)</span>
            </label>
            <div className="sd-modal-actions">
              <button className="sd-cancel-btn" onClick={closeConnectModal}>
                Cancelar
              </button>
              <button className="sd-save-btn" disabled={!relationTypeId || saving} onClick={connectSelected}>
                {saving ? 'Guardando…' : 'Guardar relación'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editar una relación existente: tipo, sentido y flecha doble */}
      {editRel && (() => {
        const baseSource = nodeLookup[editRel.sourceId];
        const baseTarget = nodeLookup[editRel.targetId];
        if (!baseSource || !baseTarget) return null;
        const sourceNode = editRel.swap ? baseTarget : baseSource;
        const targetNode = editRel.swap ? baseSource : baseTarget;
        let options = relationTypesForPair(sourceNode, targetNode);
        const currentType = relationTypes.find((rt) => String(rt.id) === String(editRel.typeId));
        if (currentType && !options.some((rt) => rt.id === currentType.id)) {
          options = [currentType, ...options];
        }
        return (
          <div className="sd-modal-backdrop" onClick={closeEditRelation}>
            <div className="sd-modal sd-edit-rel-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Editar relación</h3>
              <p className="sd-modal-sub">
                Cambia el tipo, invierte el sentido o marca una flecha doble. No hace falta borrar y volver a crear.
              </p>
              <div className="sd-direction-row">
                <span className="sd-direction-name" title={nodeDisplayName(sourceNode)}>
                  {cyLabel(nodeDisplayName(sourceNode), 26)}
                </span>
                <button
                  type="button"
                  className="sd-swap-btn"
                  onClick={() => setEditRel((r) => (r ? { ...r, swap: !r.swap } : r))}
                  title="Invertir el sentido de la relación"
                >⇄</button>
                <span className="sd-direction-name" title={nodeDisplayName(targetNode)}>
                  {cyLabel(nodeDisplayName(targetNode), 26)}
                </span>
              </div>
              <p className="sd-direction-caption">
                {nodeDisplayName(sourceNode)} {editRel.bidirectional ? '↔' : '→'} {nodeDisplayName(targetNode)}
              </p>
              <select
                value={editRel.typeId}
                onChange={(e) => setEditRel((r) => (r ? { ...r, typeId: e.target.value } : r))}
              >
                <option value="">— Elige tipo de relación —</option>
                {options.map((rt) => (
                  <option key={rt.id} value={rt.id}>{rt.display_name} ({rt.name})</option>
                ))}
              </select>
              <label className="sd-bidirect-row">
                <input
                  type="checkbox"
                  checked={editRel.bidirectional}
                  onChange={(e) => setEditRel((r) => (r ? { ...r, bidirectional: e.target.checked } : r))}
                />
                <span>Flecha doble (ida y vuelta)</span>
              </label>
              <div className="sd-modal-actions">
                <button className="sd-cancel-btn" onClick={closeEditRelation}>Cancelar</button>
                <button
                  className="sd-save-btn"
                  onClick={saveEditRelation}
                  disabled={!editRel.typeId || editRelSaving}
                >
                  {editRelSaving ? 'Guardando…' : 'Guardar cambios'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Conexiones del nodo (doble clic) */}
      {connNodeId && (() => {
        const meta = metaFor(connNode || { ci_type: '?' });
        const isMember = Boolean(connNode && connNode.member_id && String(connNode.service_id) === String(serviceId));
        const Row = ({ r }) => (
          <div className="sd-conn-row">
            {r.dir === 'out' ? (
              <>
                <span className="sd-conn-rel" style={{ color: r.catColor, borderColor: r.catColor }}>{r.relLabel}</span>
                <span className="sd-conn-arrow">→</span>
                <span className="sd-conn-other">
                  <span className="sd-conn-other-icon" style={{ background: r.otherMeta.color }}>{r.otherMeta.icon}</span>
                  <span className="sd-conn-other-txt">
                    <b>{r.otherName}</b>
                    <i>{r.otherMeta.label}</i>
                  </span>
                </span>
              </>
            ) : (
              <>
                <span className="sd-conn-other">
                  <span className="sd-conn-other-icon" style={{ background: r.otherMeta.color }}>{r.otherMeta.icon}</span>
                  <span className="sd-conn-other-txt">
                    <b>{r.otherName}</b>
                    <i>{r.otherMeta.label}</i>
                  </span>
                </span>
                <span className="sd-conn-rel" style={{ color: r.catColor, borderColor: r.catColor }}>{r.relLabel}</span>
                <span className="sd-conn-arrow">→</span>
                <span className="sd-conn-self">este elemento</span>
              </>
            )}
            {r.derived ? (
              <span className="sd-conn-auto" title="Relación automática: se oculta desde el panel Automáticas">auto</span>
            ) : (
              <button
                className="sd-conn-remove"
                onClick={() => removeRelation(r.relId, `${r.relLabel} ${r.otherName}`)}
                title={`Quitar esta conexión (${r.relLabel})`}
              >✕</button>
            )}
          </div>
        );
        return (
          <div className="sd-modal-backdrop" onClick={() => setConnNodeId(null)}>
            <div className="sd-modal sd-conn-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sd-conn-head">
                <span className="sd-conn-head-icon" style={{ background: meta.color }}>{meta.icon}</span>
                <span className="sd-conn-head-txt">
                  <h3>{nodeDisplayName(connNode) || 'Elemento'}</h3>
                  <p>{meta.label}{connNode?.role ? ` · ${connNode.role}` : ''}</p>
                </span>
                <button className="sd-model-x" onClick={() => setConnNodeId(null)} title="Cerrar (Esc)"><XMarkIcon width={20} height={20} /></button>
              </div>

              <div className="sd-conn-body">
                {nodeRelations.length === 0 && (
                  <p className="sd-conn-empty">
                    Este elemento no tiene conexiones todavía. Cierra el panel, pulsa <b>dos elementos</b> en el
                    grafo y elige el tipo de relación.
                  </p>
                )}

                {relsOut.length > 0 && (
                  <section className="sd-conn-group">
                    <p className="sd-conn-group-title">
                      <span className="sd-conn-badge" style={{ background: meta.color }}>salida</span>
                      Este elemento <b>→</b> otros <em>{relsOut.length}</em>
                    </p>
                    {relsOut.map((r) => <Row key={r.relId} r={r} />)}
                  </section>
                )}

                {relsIn.length > 0 && (
                  <section className="sd-conn-group">
                    <p className="sd-conn-group-title">
                      <span className="sd-conn-badge sd-conn-badge-in">entrada</span>
                      Otros <b>→</b> este elemento <em>{relsIn.length}</em>
                    </p>
                    {relsIn.map((r) => <Row key={r.relId} r={r} />)}
                  </section>
                )}

                {isMember && (
                  <section className="sd-conn-group sd-conn-group-member">
                    <p className="sd-conn-group-title">
                      <span className="sd-conn-badge sd-conn-badge-member">pertenencia</span>
                      Es miembro de este servicio
                    </p>
                    <div className="sd-conn-row sd-conn-row-plain">
                      <span className="sd-conn-plain-txt">Forma parte del servicio «{cyLabel(service?.display_name || service?.name, 34)}»</span>
                      <button className="sd-conn-remove" onClick={() => { setConnNodeId(null); removeNode(connNode); }} title="Quitar del servicio">✕</button>
                    </div>
                  </section>
                )}
              </div>

              <div className="sd-modal-actions sd-conn-actions">
                {onFocusCi && connNode && !connNode.is_root && !connNode.derived && !connNode.is_logical && connNode.ci_type !== 'service' && (
                  <button
                    className="sd-cancel-btn"
                    onClick={() => { const n = connNode; setConnNodeId(null); onFocusCi(n); }}
                    title="Abrir el diagrama centrado en este elemento (de qué depende y dónde impacta)"
                  >🔎 Ver su diagrama</button>
                )}
                {connNode && connNode.ci_type === 'service' && !connNode.is_root && (
                  <button
                    className="sd-cancel-btn"
                    onClick={() => { const id = connNode.ci_id; setConnNodeId(null); onOpenService && onOpenService(id, connNode); }}
                  >Abrir el servicio ↗</button>
                )}
                <button className="sd-save-btn" onClick={() => setConnNodeId(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        );
      })()}

      {showModelApply && (
        <ServiceDesignerModelApplyModal
          companyId={companyId}
          serviceId={serviceId}
          serviceName={service?.display_name || service?.name || ''}
          onClose={() => setShowModelApply(false)}
          onApplied={(result) => {
            const added = result && result.members ? result.members.length : 0;
            setNotice(`Plantilla aplicada: ${added} miembro(s) añadido(s). Puedes seguir añadiendo o quitando lo que quieras.`);
            loadGraph(depth, { silent: true });
          }}
        />
      )}

      {showHiddenPanel && (
        <div className="sd-narrative-backdrop" onClick={() => setShowHiddenPanel(false)}>
          <aside className="sd-narrative sd-hidden-panel" onClick={(e) => e.stopPropagation()}>
            <div className="sd-narrative-head">
              <h3>� Gestión de visibilidad</h3>
              <button className="sd-model-x" onClick={() => setShowHiddenPanel(false)} title="Cerrar"><XMarkIcon width={18} height={18} /></button>
            </div>
            <p className="sd-narrative-svc">
              {service?.display_name || service?.name} · {hiddenIds.length} oculto(s) de {Math.max(0, allNodes.length - 1)}
            </p>
            <p className="sd-modal-sub" style={{ marginTop: 0 }}>
              Oculta o muestra por <b>tipo</b> o elemento a elemento. Lo oculto no se borra del
              servicio; se guarda con él y vuelve al reabrir. También puedes pulsar el <b>ojo</b> de
              cada tarjeta en el grafo.
            </p>

            {/* Por tipo de CI */}
            <p className="sd-vis-section-title">Por tipo de elemento</p>
            <div className="sd-vis-types">
              {visibilityGroups.map((g) => {
                const ids = g.nodes.map((n) => n.id);
                const allHidden = g.hiddenCount === g.nodes.length;
                return (
                  <div key={g.key} className="sd-vis-type-row">
                    <span className="sd-hidden-icon" style={{ background: g.meta.color }}>{g.meta.icon}</span>
                    <span className="sd-vis-type-name">{g.key}</span>
                    <span className="sd-vis-type-count">{g.hiddenCount ? `${g.hiddenCount}/${g.nodes.length} ocultos` : `${g.nodes.length}`}</span>
                    <button
                      className={`sd-vis-toggle ${allHidden ? 'is-hidden' : ''}`}
                      onClick={() => (allHidden ? showMany(ids) : hideMany(ids))}
                    >
                      {allHidden ? 'Mostrar todos' : 'Ocultar todos'}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Por elemento */}
            <p className="sd-vis-section-title">Por elemento</p>
            <div className="sd-vis-elements">
              {visibilityGroups.map((g) => (
                <div key={`el-${g.key}`} className="sd-vis-group">
                  <p className="sd-vis-group-head">{g.meta.icon} {g.key}</p>
                  {g.nodes.map((n) => {
                    const hidden = hiddenSet.has(n.id);
                    return (
                      <div key={n.id} className={`sd-vis-item ${hidden ? 'is-hidden' : ''}`}>
                        <span className="sd-vis-item-name">
                          {n.is_logical ? `${n.icon || '🗂️'} ` : ''}{n.name || n.logical_label || n.id}
                        </span>
                        <button
                          className={`sd-vis-eye ${hidden ? 'is-hidden' : ''}`}
                          onClick={() => toggleHidden(n.id)}
                          title={hidden ? 'Mostrar' : 'Ocultar'}
                        >
                          {hidden ? '🚫 oculto' : '👁 visible'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>

            <div className="sd-narrative-actions">
              <button className="sd-cancel-btn" onClick={() => setShowHiddenPanel(false)}>Cerrar</button>
              <button className="sd-save-btn" onClick={showAllHidden} disabled={!hiddenIds.length}>Mostrar todo</button>
            </div>
          </aside>
        </div>
      )}

      {showNarrative && (
        <div className="sd-narrative-backdrop" onClick={() => setShowNarrative(false)}>
          <aside className="sd-narrative" onClick={(e) => e.stopPropagation()}>
            <div className="sd-narrative-head">
              <h3>📖 Cómo está montado</h3>
              <button className="sd-model-x" onClick={() => setShowNarrative(false)} title="Cerrar"><XMarkIcon width={18} height={18} /></button>
            </div>
            <p className="sd-narrative-svc">{service?.display_name || service?.name}</p>
            {narrative.length === 0 ? (
              <p className="sd-modal-sub">Añade elementos y relaciones para generar la explicación.</p>
            ) : (
              <div className="sd-narrative-body">
                {narrative.map((para, i) => <p key={i}>{para}</p>)}
              </div>
            )}
            <div className="sd-narrative-actions">
              <button
                className="sd-cancel-btn"
                onClick={() => { navigator.clipboard && navigator.clipboard.writeText(narrative.join('\n\n')); setNotice('Explicación copiada al portapapeles.'); }}
              >
                Copiar texto
              </button>
              <button className="sd-save-btn" onClick={() => setShowNarrative(false)}>Cerrar</button>
            </div>
          </aside>
        </div>
      )}

      {/* Aviso al desplegar un grupo grande: elegir qué miembros mostrar. */}
      {expandPrompt && (
        <div className="sd-modal-backdrop" onClick={() => setExpandPrompt(null)}>
          <div className="sd-modal sd-expand-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Desplegar «{nodeLookup[expandPrompt.groupId]?.logical_label || 'grupo'}»</h3>
            <p className="sd-modal-sub">
              Este grupo tiene <b>{expandPrompt.members.length}</b> elementos. Elige los que quieres ver:
              el resto se ocultará para que el diagrama no se sature.
            </p>
            <div className="sd-type-modal-actions">
              <button type="button" className="sd-cancel-btn"
                onClick={() => setExpandPrompt((p) => (p ? { ...p, selected: [...p.members] } : p))}>
                Seleccionar todo
              </button>
              <button type="button" className="sd-cancel-btn"
                onClick={() => setExpandPrompt((p) => (p ? { ...p, selected: [] } : p))}>
                Ninguno
              </button>
            </div>
            <div className="sd-expand-list">
              {expandPrompt.members.map((id) => {
                const n = nodeLookup[id];
                if (!n) return null;
                const mm = metaFor(n);
                return (
                  <label key={id} className="sd-expand-row">
                    <input type="checkbox" checked={expandPrompt.selected.includes(id)}
                      onChange={() => toggleExpandSelected(id)} />
                    <span className="sd-detail-list-dot" style={{ background: mm.color }}>{mm.icon}</span>
                    <span className="sd-expand-name">{n.name}</span>
                    <em className="sd-expand-type">{mm.label}</em>
                  </label>
                );
              })}
            </div>
            <div className="sd-modal-actions">
              <button className="sd-cancel-btn" onClick={() => setExpandPrompt(null)}>Cancelar</button>
              <button className="sd-save-btn" onClick={confirmExpand} disabled={!expandPrompt.selected.length}>
                Desplegar {expandPrompt.selected.length}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flechas automáticas: listado y visibilidad persistente por servicio */}
      {showDerivedPanel && (
        <div className="sd-modal-backdrop" onClick={() => setShowDerivedPanel(false)}>
          <div className="sd-modal sd-derived-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Flechas automáticas</h3>
            <p className="sd-modal-sub">
              Las genera el diseñador a partir de las fichas (por ejemplo, VM → hypervisor o equipo → cabina).
              Desmarca las que no quieras ver; se guarda para la próxima vez que entres.
            </p>
            <div className="sd-type-modal-actions">
              <button type="button" className="sd-cancel-btn" onClick={() => setAllDerivedVisible(true)}>Mostrar todas</button>
              <button type="button" className="sd-cancel-btn" onClick={() => setAllDerivedVisible(false)}>Ocultar todas</button>
            </div>
            <div className="sd-derived-list">
              {derivedEdges.map((e) => {
                const visible = !hiddenDerivedSet.has(e.id);
                return (
                  <label key={e.id} className={`sd-derived-row ${visible ? '' : 'is-off'}`}>
                    <input type="checkbox" checked={visible} onChange={() => toggleDerived(e.id)} />
                    <span className="sd-derived-rel">{e.label}</span>
                    <span className="sd-derived-names">{e.fromName} → {e.toName}</span>
                  </label>
                );
              })}
              {derivedEdges.length === 0 && (
                <p className="sd-modal-sub">Este servicio no tiene flechas automáticas.</p>
              )}
            </div>
            <div className="sd-modal-actions">
              <button className="sd-save-btn" onClick={() => setShowDerivedPanel(false)}>Listo</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de tipos del catálogo. Sustituye la fila de chips por un botón. */}
      {showTypeModal && (
        <div className="sd-modal-backdrop" onClick={() => setShowTypeModal(false)}>
          <div className="sd-modal sd-type-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Tipos de elemento</h3>
            <p className="sd-modal-sub">
              Marca los tipos que quieres ver en «Elementos de la empresa». La búsqueda por nombre sigue igual.
            </p>
            <div className="sd-type-modal-actions">
              <button type="button" className="sd-cancel-btn" onClick={() => setAllTypes(true)}>Seleccionar todo</button>
              <button type="button" className="sd-cancel-btn" onClick={() => setAllTypes(false)}>Deseleccionar todo</button>
            </div>
            <div className="sd-type-filters sd-type-filters-modal">
              {SEARCHABLE_TYPES.map((t) => {
                const on = !hiddenTypes[t];
                const mt = metaFor({ ci_type: t, kind: t });
                return (
                  <label key={t} className={`sd-type-chip ${on ? 'on' : ''}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleType(t)} />
                    <span className="sd-type-chip-dot" style={{ background: mt.color }}>{mt.icon}</span>
                    <span className="sd-type-chip-label">{typeLabel(t)}</span>
                  </label>
                );
              })}
            </div>
            <div className="sd-modal-actions">
              <button className="sd-save-btn" onClick={() => setShowTypeModal(false)}>Listo</button>
            </div>
          </div>
        </div>
      )}

      {/* Detalle del elemento (clic en el nombre de la tarjeta) */}
      {detailNode && (
        <div className="sd-detail-backdrop" onClick={closeDetail}>
          <aside className="sd-detail" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const m = metaFor(detailNode);
              if (detailNode.is_logical) {
                const isCard = detailNode.icon === '🗓️';
                const memberIds = groupMembers[detailNode.id] || [];
                const members = memberIds.map((id) => nodeLookup[id]).filter(Boolean);
                const parts = String(detailNode.detail || '')
                  .split(' · ').map((s) => s.trim()).filter(Boolean);
                const pick = (re) => parts.find((p) => re.test(p)) || null;
                const schedule = pick(/^(Sin programar|Diario|Semanal|Mensual|Cada\b|.*\b\d{1,2}:\d{2})/i);
                const equipos = pick(/\bequipos?\b/i);
                const trabajos = pick(/\btrabajos?\b/i);
                const retencion = pick(/\bd[ií]as?\b/i);
                const software = parts.find((p) => p !== schedule && p !== equipos && p !== trabajos && p !== retencion) || null;
                const policyEdge = (graph?.edges || []).find((e) => e.relation_type === 'has_policy' && e.target === detailNode.id);
                const cardServiceId = policyEdge ? Number(String(policyEdge.source).split(':')[1]) : serviceId;
                const groupEdge = (graph?.edges || []).find((e) => e.relation_type === 'applies_to' && e.source === detailNode.id);
                const groupNode = groupEdge ? nodeLookup[groupEdge.target] : null;
                const groupIsCollapsed = collapsedSet.has(detailNode.id);
                return (
                  <div className="sd-detail-inner">
                    <div className="sd-detail-head">
                      <span className="sd-detail-icon" style={{ background: m.color }}>{detailNode.icon || m.icon}</span>
                      <div className="sd-detail-title">
                        <h3>{detailNode.logical_label || 'Elemento lógico'}</h3>
                        <p>{isCard ? 'Resumen de copia de seguridad' : `${members.length} elemento${members.length === 1 ? '' : 's'} protegido${members.length === 1 ? '' : 's'}`}</p>
                      </div>
                      <button className="sd-model-x" onClick={closeDetail} title="Cerrar"><XMarkIcon width={18} height={18} /></button>
                    </div>

                    {isCard ? (
                      <>
                        <div className="sd-detail-rows">
                          <div className="sd-detail-row">
                            <span className="sd-detail-label">Software</span>
                            <span className="sd-detail-value">{software || '—'}</span>
                          </div>
                          <div className="sd-detail-row">
                            <span className="sd-detail-label">Programación</span>
                            <span className="sd-detail-value">{schedule || 'Sin programar'}</span>
                          </div>
                          {retencion && (
                            <div className="sd-detail-row">
                              <span className="sd-detail-label">Retención</span>
                              <span className="sd-detail-value">{retencion}</span>
                            </div>
                          )}
                          <div className="sd-detail-row">
                            <span className="sd-detail-label">Equipos</span>
                            <span className="sd-detail-value">{equipos || '—'}</span>
                          </div>
                          <div className="sd-detail-row">
                            <span className="sd-detail-label">Trabajos</span>
                            <span className="sd-detail-value">{trabajos || '—'}</span>
                          </div>
                        </div>
                        {!schedule || /sin programar/i.test(schedule) ? (
                          <div className="sd-detail-note">
                            ℹ️ Este servicio no tiene trabajos con programación: la tarjeta resume las relaciones manuales del diseñador. Si creas políticas reales, aquí aparecerá su horario y retención.
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <div className="sd-detail-rows">
                          {members.map((member) => {
                            const mm = metaFor(member);
                            const visible = !hiddenSet.has(member.id);
                            return (
                              <label key={member.id} className="sd-detail-list-row" title={visible ? 'Visible en el diagrama' : 'Oculto en el diagrama'}>
                                <input type="checkbox" checked={visible} onChange={() => toggleHidden(member.id)} />
                                <span className="sd-detail-list-dot" style={{ background: mm.color }}>{mm.icon}</span>
                                <span className="sd-detail-list-name">{member.name}</span>
                                <em className="sd-detail-list-type">{mm.label}</em>
                                <span className="sd-detail-list-actions">
                                  <button
                                    type="button"
                                    className="sd-detail-mini-btn"
                                    disabled={readOnly}
                                    title="Sacar del grupo (se mantiene en el diseño)"
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      removeNodeFromGroup(detailNode.id, member);
                                    }}
                                  >
                                    <ArrowLeftOnRectangleIcon width={13} height={13} />
                                  </button>
                                  {member.member_id ? (
                                    <button
                                      type="button"
                                      className="sd-detail-mini-btn danger"
                                      disabled={readOnly}
                                      title="Quitar del diseño (elimina el miembro del servicio)"
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        removeNode(member);
                                      }}
                                    >
                                      <TrashIcon width={13} height={13} />
                                    </button>
                                  ) : null}
                                </span>
                              </label>
                            );
                          })}
                          {!members.length && <p className="sd-modal-sub">Este grupo no tiene miembros.</p>}
                        </div>
                        {members.length > 0 && (
                          <div className="sd-detail-actions">
                            <button className="sd-cancel-btn" onClick={() => showMany(members.map((x) => x.id))}>Mostrar todos</button>
                            <button className="sd-cancel-btn" onClick={() => hideMany(members.map((x) => x.id))}>Ocultar todos</button>
                          </div>
                        )}
                      </>
                    )}

                    {/* Contactos del elemento. En el grafo no se dibujan
                        nodos de contacto (no son CIs): se enseñan aquí, en el
                        panel de detalle del nodo, y se pueden vincular sin
                        salir del diseñador. */}
                    {!detailNode.is_logical && !isCard && detailNode.ci_id && (
                      <div style={{ marginTop: 12, borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                        <ContactsPanel
                          companyId={companyId}
                          objectType={
                            detailNode.ci_type === 'rack_element'
                              ? (detailNode.kind === 'switch' ? 'switch' : 'server')
                              : detailNode.ci_type
                          }
                          objectId={detailNode.ci_id}
                          objectName={detailNode.name}
                          compact
                        />
                      </div>
                    )}

                    <div className="sd-detail-actions">
                      {isCard ? (
                        <button className="sd-save-btn" onClick={() => { closeDetail(); navigate(`/backups?software=${cardServiceId}`); }}>
                          Abrir Políticas de Backup ↗
                        </button>
                      ) : (
                        <button className="sd-save-btn" onClick={() => {
                          const id = detailNode.id;
                          closeDetail();
                          if (groupIsCollapsed) requestExpandGroup(id);
                          else toggleCollapsed(id);
                        }}>
                          {groupIsCollapsed ? 'Desplegar en el diagrama' : 'Plegar en el diagrama'}
                        </button>
                      )}
                      {isCard && groupNode && (
                        <button className="sd-cancel-btn" onClick={() => openDetail(groupNode)}>
                          Ver equipos protegidos
                        </button>
                      )}
                      <button className="sd-cancel-btn" onClick={closeDetail}>Cerrar</button>
                    </div>
                  </div>
                );
              }
              const info = detailInfo || {};
              const type = detailNode.ci_type;
              const FIELDS = {
                rack_element: [
                  ['name', 'Nombre'], ['element_type', 'Tipo de elemento'], ['model', 'Modelo'],
                  ['serial_number', 'Nº de serie'], ['rack_id', 'Rack'], ['position_u', 'Posición (U)'],
                  ['status', 'Estado'], ['notes', 'Notas'],
                ],
                virt_vm: [
                  ['name', 'Nombre'], ['fqdn', 'FQDN'], ['os_name', 'Sistema'], ['os_version', 'Versión'],
                  ['cpu_cores', 'CPUs'], ['ram_gb', 'RAM (GB)'], ['environment', 'Entorno'], ['status', 'Estado'],
                  ['hypervisor_name', 'Hypervisor'], ['cluster_name', 'Granja / Cluster'], ['notes', 'Notas'],
                ],
                firewall: [
                  ['name', 'Nombre'], ['deployment_type', 'Despliegue'], ['vendor', 'Fabricante'], ['model', 'Modelo'],
                  ['throughput_gbps', 'Throughput (Gbps)'], ['ha_enabled', 'HA'], ['status', 'Estado'], ['notes', 'Notas'],
                ],
                service: [
                  ['name', 'Nombre'], ['display_name', 'Nombre visible'], ['service_type', 'Tipo'],
                  ['criticality', 'Criticidad'], ['status', 'Estado'], ['description', 'Descripción'], ['notes', 'Notas'],
                ],
                virt_hypervisor: [
                  ['name', 'Nombre'], ['hypervisor_type_label', 'Tipo'], ['rack_element_name', 'Servidor físico'],
                  ['mgmt_ip', 'IP de gestión'], ['cpu_total_cores', 'CPUs'], ['ram_total_gb', 'RAM (GB)'],
                  ['ha_mode', 'Modo HA'], ['status', 'Estado'], ['notes', 'Notas'],
                ],
                rack: [
                  ['name', 'Nombre'], ['code', 'Código'], ['height_u', 'Altura (U)'], ['manufacturer', 'Fabricante'],
                  ['model', 'Modelo'], ['serial_number', 'Nº de serie'], ['description', 'Descripción'], ['notes', 'Notas'],
                ],
                ipam_subnet: [
                  ['cidr', 'CIDR'], ['name', 'Nombre'], ['type_label', 'Tipo'],
                  ['vlan_number', 'VLAN'], ['vlan_name', 'Nombre de la VLAN'],
                  ['gateway', 'Puerta de enlace'], ['site', 'Sede'], ['status', 'Estado'],
                  ['description', 'Descripción'],
                ],
                wifi_controller: [
                  ['name', 'Nombre'], ['model', 'Modelo'], ['hostname', 'Hostname'],
                  ['mgmt_ip', 'IP de gestión'], ['status', 'Estado'], ['notes', 'Notas'],
                ],
                wifi_access_point: [
                  ['name', 'Nombre'], ['model', 'Modelo'], ['band', 'Banda'],
                  ['management_ip', 'IP de gestión'], ['mac_address', 'MAC'], ['status', 'Estado'],
                ],
                wifi_ssid: [
                  ['name', 'SSID'], ['vlan_id', 'VLAN'], ['security', 'Seguridad'],
                  ['band', 'Banda'], ['status', 'Estado'],
                ],
                virt_cluster: [
                  ['name', 'Nombre'], ['cluster_type', 'Tipo'], ['status', 'Estado'], ['notes', 'Notas'],
                ],
                database_instance: [
                  ['name', 'Instancia'], ['dbms', 'Motor'], ['version', 'Versión'],
                  ['port', 'Puerto'], ['role', 'Rol'], ['status', 'Estado'],
                  ['host_vm_name', 'Máquina virtual'], ['host_server_name', 'Servidor físico'],
                  ['data_dir', 'Directorio de datos'],
                  ['copy_method', 'Método de copia'], ['source_instance_name', 'Copia de'],
                  ['replication_target', 'Replica a'], ['notes', 'Notas'],
                ],
                instance_database: [
                  ['name', 'Base de datos'], ['instance_name', 'Instancia'],
                  ['environment_label', 'Entorno'], ['size_gb', 'Tamaño (GB)'],
                  ['collation_name', 'Collation'], ['charset', 'Juego de caracteres'],
                  ['purpose', 'Qué aplicación la usa'], ['status', 'Estado'], ['notes', 'Notas'],
                ],
                // Nodo sintético del alojamiento de una VM delegada: no tiene
                // ficha propia, todo lo que se puede decir viene del grafo.
                external_infra: [
                  ['name', 'Nombre'], ['role', 'Rol'], ['detail', 'Detalle'],
                ],
              };
              const fields = FIELDS[type] || [['name', 'Nombre'], ['status', 'Estado']];
              const val = (k) => {
                let v = info ? info[k] : undefined;
                // Los nodos derivados traen sus datos en el propio grafo, no en
                // una ficha: sin este respaldo salían todos los campos a «—».
                if ((v === undefined || v === null) && detailNode[k] !== undefined) v = detailNode[k];
                if ((v === undefined || v === null) && k === 'name') v = detailNode.name;
                // La VM delegada no expone el hipervisor: el hueco es
                // deliberado, no un dato que falte. Si declara plataforma, se
                // nombra la plataforma (L6 Ecosystem, Azure…) y no el genérico.
                if (k === 'hypervisor_name' && detailNode.hosting_hidden) {
                  return detailNode.hosting_provider_label || 'Infraestructura externa';
                }
                if (typeof v === 'boolean') return v ? 'Sí' : 'No';
                if (typeof v === 'object') return v ? JSON.stringify(v) : '—';
                return (v === undefined || v === null || v === '') ? '—' : String(v);
              };
              const rows = fields.map(([k, label]) => ({ label, value: val(k) }));
              const nics = Array.isArray(info.nics) ? info.nics : [];
              const ips = Array.isArray(detailIps) ? detailIps : [];
              const route = ciRoute(detailNode);
              return (
                <div className="sd-detail-inner">
                  <div className="sd-detail-head">
                    <span className="sd-detail-icon" style={{ background: m.color }}>{detailNode.icon || m.icon}</span>
                    <div className="sd-detail-title">
                      <h3 title={detailNode.name || ''}>{cyLabel(detailNode.name || String(detailNode.ci_id), 46)}</h3>
                      {detailNode.is_logical ? (
                        <p>{detailNode.detail ? `${detailNode.detail} · ` : ''}Grupo lógico</p>
                      ) : (
                        <p>{detailNode.role ? `${detailNode.role} · ` : ''}{m.label}{detailNode.ci_id != null ? ` #${detailNode.ci_id}` : ''}</p>
                      )}
                    </div>
                    <button className="sd-model-x" onClick={closeDetail} title="Cerrar"><XMarkIcon width={18} height={18} /></button>
                  </div>
                  {detailLoading ? (
                    <p className="sd-modal-sub">Cargando detalle…</p>
                  ) : (
                    <>
                      {detailError && (
                        <div className="sd-detail-warn">
                          ⚠️ {detailError} Se muestran solo los datos del mapa.
                        </div>
                      )}
                      {detailNote && !detailError && (
                        <div className="sd-detail-note">ℹ️ {detailNote}</div>
                      )}
                      <div className="sd-detail-rows">
                        {rows.map((r, i) => (
                          <div key={i} className="sd-detail-row">
                            <span className="sd-detail-label">{r.label}</span>
                            <span className="sd-detail-value">{r.value}</span>
                          </div>
                        ))}
                      </div>

                      {/* Lo que se respondio en el asistente: hasta ahora se guardaba en
                          Service.config_json y no se pintaba en ninguna parte. */}
                      {type === 'service' && info.config_json && Object.keys(info.config_json).length > 0 && (
                        <div className="sd-detail-section">
                          <p className="sd-detail-section-title">🧾 Datos del asistente</p>
                          <ServiceConfigView config={info.config_json} showHint={false} compact />
                        </div>
                      )}

                      {(nics.length > 0 || ips.length > 0) && (
                        <div className="sd-detail-section">
                          <p className="sd-detail-section-title">🌐 Configuración de red</p>
                          {nics.map((nic) => (
                            <div key={`nic-${nic.id}`} className="sd-detail-net">
                              <span className="sd-detail-net-key">
                                {nic.nic_label || 'NIC'}{nic.connected === false ? ' (desconectada)' : ''}
                              </span>
                              <span className="sd-detail-net-val">
                                {nic.ip_address || '—'}
                                {nic.mac_address ? <em>{nic.mac_address}</em> : null}
                                {nic.port_group_name ? <em>{nic.port_group_name}{nic.vlan_id != null ? ` · VLAN ${nic.vlan_id}` : ''}</em> : null}
                              </span>
                            </div>
                          ))}
                          {ips.map((a) => (
                            <div key={`ip-${a.id}`} className="sd-detail-net">
                              <span className="sd-detail-net-key">
                                {a.interface_ref || (a.is_primary ? 'principal' : 'IP')}
                              </span>
                              <span className="sd-detail-net-val">
                                <b>{a.ip}</b>
                                {a.subnet_cidr ? <em>{a.subnet_cidr}</em> : null}
                                {a.dns_name ? <em>{a.dns_name}</em> : null}
                                {a.vlan_number != null ? <em>VLAN {a.vlan_number}{a.vlan_name ? ` ${a.vlan_name}` : ''}</em> : null}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                  <div className="sd-detail-actions">
                    <button className="sd-cancel-btn" onClick={() => { const id = detailNode.id; closeDetail(); openNodeConnections(id); }}>
                      🔗 Ver conexiones
                    </button>
                    {onFocusCi && detailNode && !detailNode.is_root && !detailNode.derived && !detailNode.is_logical && detailNode.ci_type !== 'service' && (
                      <button className="sd-cancel-btn" onClick={() => { const n = detailNode; closeDetail(); onFocusCi(n); }}>
                        🔎 De qué depende / dónde impacta
                      </button>
                    )}
                    {route && (
                      <button className="sd-save-btn" onClick={goDetailModule}>Abrir en CMDB ↗</button>
                    )}
                    <button className="sd-cancel-btn" onClick={closeDetail}>Cerrar</button>
                  </div>
                </div>
              );
            })()}
          </aside>
        </div>
      )}
    </div>
  );
}
